import { Component, HostListener, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { PersonalHistoryDiseasePipe } from '../../pipes/personal-history-disease.pipe';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  DiseaseOption,
  QuestionnaireForm,
  Questions,
  AppointmentInfoResponse,
  QuestionnaireStatus,
  AppointmentStatus,
  SaveQuestionnaireRequest,
  QuestionAnswers,
  UserPlanRequest,
} from '../../shared/interfaces/reserve-health.model';
import {
  HEALTH_QUESTIONNAIRE_FIELDS,
  RESIDENCE_COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  EDUCATION_LEVEL_OPTIONS,
  POSITION_OPTIONS,
  SHIFT_TYPE_OPTIONS,
  EXAM_REASON_OPTIONS,
  CURRENT_OCCUPATION_CATEGORY_OPTIONS,
  PREVIOUS_OCCUPATION_CATEGORY_OPTIONS,
  NEEDS_SHIFT_OPTIONS,
  NONE_OF_FOLLOWING,
} from '../../shared/constants/health-questionnaire-fields.constants';
import { NgClass, ViewportScroller } from '@angular/common';
import { FamilyHistoryDiseasePipe } from '../../pipes/family-history-disease.pipe';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { ReserveHealthService } from '../../shared/services/reserve-health.service';
import { AuthService } from '../../shared/services/auth.service';
import { SubjectiveSymptomDiseasePipe } from '../../pipes/subjective-symptom-disease.pipe';
import { SingleActionDialogComponent } from '../../shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { API_PARAMS } from '../../shared/constants/api.constants';
import moment from 'moment';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { DATE_FORMAT } from '../../shared/constants/date.constants';
import { Observable } from 'rxjs';
import { DuoActionDialogComponent } from '../../shared/components/dialogs/duo-action-dialog/duo-action-dialog.component';
import { DuoActionDialogModel } from '../../shared/interfaces/dialogs/duo-action-dialog.model';
import { ToastrService } from 'ngx-toastr';

export interface ComponentCanDeactivate {
  canDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
}

/**
 * 會員中心健康調查表組件
 */
@Component({
  selector: 'app-health-questionnaire',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    MatCheckbox,
    MatError,
    MatInput,
    PersonalHistoryDiseasePipe,
    ReactiveFormsModule,
    FormsModule,
    NgClass,
    FamilyHistoryDiseasePipe,
    SubjectiveSymptomDiseasePipe,
    MatRadioGroup,
    MatRadioButton,
    MatSelect,
    MatOption,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
  ],
  templateUrl: './health-questionnaire.component.html',
  styleUrl: './health-questionnaire.component.scss',
})
export class HealthQuestionnaireComponent implements OnInit, ComponentCanDeactivate {
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  reserveHealthService: ReserveHealthService = inject(ReserveHealthService);
  translateService: TranslateService = inject(TranslateService);
  authService: AuthService = inject(AuthService);
  dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  constructor(private viewportScroller: ViewportScroller) {}

  // 將常數暴露給 template 使用
  readonly API_PARAMS = API_PARAMS;
  readonly DATE_FORMAT = DATE_FORMAT;
  
  // 如果 template 需要使用這些選項陣列
  readonly CURRENT_OCCUPATION_CATEGORY_OPTIONS = CURRENT_OCCUPATION_CATEGORY_OPTIONS;
  readonly PREVIOUS_OCCUPATION_CATEGORY_OPTIONS = PREVIOUS_OCCUPATION_CATEGORY_OPTIONS;
  readonly SHIFT_TYPE_OPTIONS = SHIFT_TYPE_OPTIONS;
  readonly NEEDS_SHIFT_OPTIONS = NEEDS_SHIFT_OPTIONS;
  readonly EXAM_REASON_OPTIONS = EXAM_REASON_OPTIONS;
  readonly RESIDENCE_COUNTRY_OPTIONS = RESIDENCE_COUNTRY_OPTIONS;
  readonly MARITAL_STATUS_OPTIONS = MARITAL_STATUS_OPTIONS;
  readonly EDUCATION_LEVEL_OPTIONS = EDUCATION_LEVEL_OPTIONS;
  readonly POSITION_OPTIONS = POSITION_OPTIONS;

  // 健調表顯示狀態
  questionnaireStatus: QuestionnaireStatus = 'AVAILABLE';

  // 預約資料（從 CB-007 API 取得）
  appointmentInfo?: AppointmentInfoResponse;

  /**
   * 健調表問卷步驟
   * 1: 個人病史
   * 2: 家族病史
   * 3: 自覺症狀
   * 4: 長期服用藥物
   * 5: 手術史或重大外傷
   * 6: 生活習慣
   * 7: 作業經歷
   * 8: 其他資訊
   */
  step = 1;
  showError: boolean = false; // 是否顯示錯誤訊息
  lifeStyleErrors: {[key: number]: boolean} = {}; // 生活習慣每題的錯誤狀態
  exerciseHabitErrors: {[key: number]: boolean} = {}; // 運動習慣每題的錯誤狀態

  showPersonalHistoryError: boolean = false; // 個人病史錯誤狀態
  showFamilyHistoryError: boolean = false; // 家族病史錯誤狀態
  showSubjectiveSymptomError: boolean = false; // 自覺症狀錯誤狀態
  showLongTermMedicationError: boolean = false; // 長期服用藥物錯誤狀態

  personalHistoryDiseasePipe: PersonalHistoryDiseasePipe =
    new PersonalHistoryDiseasePipe();
  familyHistoryDiseasePipe: FamilyHistoryDiseasePipe =
    new FamilyHistoryDiseasePipe();
  subjectiveSymptomDiseasePipe: SubjectiveSymptomDiseasePipe =
    new SubjectiveSymptomDiseasePipe();

  personalNoneOfFollowing: boolean = false; // 個人病史"以下皆無"是否勾選
  personalQuestionnaire?: QuestionnaireForm; // 個人病史欄位
  personalDiseases: Array<string> = []; // 勾選之"個人病史"欄位 交由下一階段個人方案結果頁面中 CB-046 來傳送API請求
  personalDiseaseDetails: { [code: string]: string } = {}; // 個人病史詳細症狀，使用者對各特別項目填寫的答案。 在階段提交後會將 personalDiseaseDetails的詳細資料轉換並存入 personalDiseases
  personalDiseaseCheck: Questions = { questionId: '474', questionAnswers: [] };
  otherPersonalDisease?: string; // 個人病史其他症狀

  familyNoneOfFollowing: boolean = false; // 個人病史"以下皆無"是否勾選
  familyQuestionnaire?: QuestionnaireForm; // 家族病史欄位
  familyDiseases: Array<string> = []; // 勾選之"家族病史"欄位
  familyDiseaseDetails: { [code: string]: string } = {}; // 家族病史詳細症狀
  familyDiseaseCheck: Questions = { questionId: '483', questionAnswers: [] };
  otherFamilyDisease?: string; // 家族病史其他症狀

  subjectiveSymptomNoneOfFollowing: boolean = false; // 自覺症狀"以下皆無"是否勾選
  subjectiveSymptomQuestionnaire?: QuestionnaireForm; // 自覺症狀欄位
  subjectiveSymptomDiseases: Array<string> = []; // 勾選之"自覺症狀"欄位
  subjectiveSymptomDiseaseCheck: Questions = {
    questionId: '472',
    questionAnswers: [],
  };
  otherSubjectiveSymptomDisease?: string; // 自覺其他症狀

  longTermMedicationNoneOfFollowing: boolean = false; // 長期服用藥物"以下皆無"是否勾選
  longTermMedicationQuestionnaire?: QuestionnaireForm; // 長期服用藥物欄位
  longTermMedications: Array<string> = []; // 勾選之"長期服用藥物"欄位
  longTermMedicationCheck: Questions = { questionId: '481', questionAnswers: [] }; // TODO 長期服用藥物的questionId
  otherLongTermMedication?: string; // 其他長期服用藥物
  hasOtherLongTermMedication: boolean = false; // 是否有其他長期服用藥物

  hasSurgeryHistoryOrMajorTrauma?: string; // 是否有手術史或重大外傷
  surgeryHistoryOrMajorTrauma?: string; // 手術史或重大外傷名稱

  lifeStyleHabitQuestionnaire?: QuestionnaireForm; // 生活習慣欄位
  selectedLifeStyleHabits: { [key: number]: string } = {}; // 儲存每組選中的值
  lifeStyleHabits: Array<string> = []; // 勾選之"生活習慣"欄位
  lifeStyleHabitCheck: Questions = { questionId: '', questionAnswers: [] }; // TODO 生活習慣的questionId

  exerciseHabitQuestionnaire?: QuestionnaireForm; // 運動習慣欄位
  selectedExerciseHabits: { [key: number]: string } = {}; // 儲存每組選中的值
  exerciseHabits: Array<string> = []; // 勾選之"運動習慣"欄位
  exerciseHabitCheck: Questions = { questionId: '', questionAnswers: [] }; // TODO 運動習慣的questionId
  hasExerciseHabit: string = ''; // 是否有運動習慣

  showResidenceCountryError: boolean = false; // 常住國家錯誤狀態
  showMaritalStatusError: boolean = false; // 婚姻狀況錯誤狀態
  showEducationLevelError: boolean = false; // 教育程度錯誤狀態
  showPositionError: boolean = false; // 職位錯誤狀態

  // 手術史相關
  surgeryHistoryCheck: Questions = { questionId: '479', questionAnswers: [] };

  // 其他資訊相關
  residenceCountryCheck: Questions = { questionId: '511', questionAnswers: [] };
  maritalStatusCheck: Questions = { questionId: '513', questionAnswers: [] };
  educationLevelCheck: Questions = { questionId: '514', questionAnswers: [] };
  positionCheck: Questions = { questionId: '515', questionAnswers: [] };

  // 目前從事職業表單
  occupation = new FormGroup({
    category: new FormControl('', [Validators.required]),
    otherOccupation: new FormControl('', [Validators.required]),
    employmentDate: new FormControl<moment.Moment | null>(null, [
      Validators.required,
    ]),
    employmentEndDate: new FormControl<moment.Moment | null>(null, [
      Validators.required,
    ]),
    type: new FormControl('', [Validators.required]),
    otherShifts: new FormControl('', [Validators.required]),
    shiftDescription: new FormControl(''),
  });
  // 曾經從事職業表單
  previousOccupation = new FormGroup({
    category: new FormControl('', [Validators.required]),
    otherOccupation: new FormControl('', [Validators.required]),
    startYear: new FormControl('', [Validators.required]),
    startMonth: new FormControl('', [Validators.required]),
    endYear: new FormControl('', [Validators.required]),
    endMonth: new FormControl('', [Validators.required]),
    avgWorkHoursPastMonth: new FormControl(0, [Validators.min(0)]),
    avgWorkHoursPastSixMonths: new FormControl(0, [Validators.min(0)]),
    avgSleepTime: new FormControl(0, [Validators.min(0)]),
    examReason: new FormControl('', [Validators.required]),
  });
  years: Array<string> = Array.from(
    { length: new Date().getFullYear() - 1900 + 1 },
    (_, i) => String(1900 + i)
  );
  months: Array<string> = Array.from({ length: 12 }, (_, i) => String(i + 1));

  residenceCountries: Array<string> = []; // 勾選的居住國家列表
  otherResidenceCountry?: string; // 其他居住國家
  maritalStatus: string = ''; // 婚姻狀況
  educationLevel: string = ''; // 教育程度
  position: string = ''; // 職位
  otherPosition?: string; // 其他職稱

  // 生活習慣選項不可輸入負數驗證
  betelNutPerDay = new FormControl(0, [Validators.min(0)]);
  betelNutYears = new FormControl(0, [Validators.min(0)]);
  betelNutMonths = new FormControl(0, [Validators.min(0), Validators.max(12)]);
  betelNutQuitYears = new FormControl(0, [Validators.min(0)]);
  betelNutQuitMonths = new FormControl(0, [Validators.min(0), Validators.max(12)]);

  cigarettesPerDay = new FormControl(0, [Validators.min(0)]);
  smokingYears = new FormControl(0, [Validators.min(0)]);
  smokingQuitYears = new FormControl(0, [Validators.min(0)]);
  smokingQuitMonths = new FormControl(0, [Validators.min(0), Validators.max(12)]);

  alcoholTimesPerWeek = new FormControl(0, [Validators.min(0)]);
  alcoholType = new FormControl('');
  alcoholAmountCc = new FormControl(0, [Validators.min(0)]);
  alcoholQuitYears = new FormControl(0, [Validators.min(0)]);
  alcoholQuitMonths = new FormControl(0, [Validators.min(0), Validators.max(12)]);

  isFormDirty: boolean = false;
  private allowNavigation: boolean = false;

  // 是否已經預先設定過狀態（避免重複計算造成閃爍）
  hasPresetStatus: boolean = false;

  // 載入狀態
  isLoading: boolean = true;

  /**
   * 預先檢查問卷狀態，避免閃爍未完成 tag
   * 策略：如果有預約資料，先設定為 WAITING 狀態，避免顯示 AVAILABLE
   */
  private preCheckQuestionnaireStatus(): void {
    try {
      console.log('Health-questionnaire: 開始預先檢查問卷狀態');

      // 檢查是否已經有完成的問卷資料
      const savedData = this.sharedStorageService.get('savedQuestionnaireData');
      const myReservation = this.sharedStorageService.get('myReservation');

      console.log('預先檢查資料:', {
        hasSavedData: !!savedData,
        hasSavedAt: !!savedData?.savedAt,
        savedCaseNo: savedData?.caseNo,
        hasReservation: !!myReservation,
        reservationCaseNo: myReservation?.caseNo,
        reservationBookDate: myReservation?.bookDate,
        reservationBookStatus: myReservation?.bookStatus,
        caseNoMatch: savedData?.caseNo === myReservation?.caseNo
      });

      // 先檢查 SharedStorage 中的已完成資料
      if (savedData?.savedAt && savedData?.caseNo === myReservation?.caseNo) {
        // 檢查問卷是否真正完整（8頁都填寫完成）
        const isComplete = this.checkIfQuestionnaireIsComplete(savedData);

        if (isComplete) {
          // 有完整的資料，立即設定為 COMPLETED 避免閃爍
          this.questionnaireStatus = 'COMPLETED';
          this.reserveHealthService.updateQuestionnaireStatus('COMPLETED');
          console.log('預先檢查：發現完整問卷資料（8頁都完成），立即設定狀態為 COMPLETED');

          // 標記已經預先設定過狀態，避免後續重複計算造成閃爍
          this.hasPresetStatus = true;
          return;
        } else {
          console.log('預先檢查：發現不完整問卷資料，讓原本邏輯處理');
        }
      }

      // 如果沒有完整資料，不要預先設定狀態，讓原本的 determineQuestionnaireStatus 邏輯處理

      // 不設定 hasPresetStatus，讓後續的 determineQuestionnaireStatus 正常執行
      if (myReservation?.bookDate && myReservation?.bookStatus) {
        // 使用 service 的初始狀態
        this.questionnaireStatus = this.reserveHealthService.getCurrentQuestionnaireStatus();
        console.log('預先檢查：沒有預約資料，使用 service 初始狀態:', this.questionnaireStatus);
      }
    } catch (error) {
      console.error('預先檢查問卷狀態失敗:', error);
      // 發生錯誤時使用 service 的狀態
      this.questionnaireStatus = this.reserveHealthService.getCurrentQuestionnaireStatus();
    }
  }

  ngOnInit(): void {
    // 先檢查是否已經有完成的問卷資料，避免閃爍未完成 tag
    this.preCheckQuestionnaireStatus();

    // this.getAppointmentInfo(); // 取得預約資料(CB-007 API)
    // this.loadAppointmentInfo(); // 取得預約資料(假資料修改UI用)
    this.loadAppointmentInfoFromSharedStorage(); // 取得預約資料(從 SharedStorage 取得)

    if (this.authService.isLogin()) {
      const idNo =
        this.sharedStorageService.get('idLoginRequest')?.idNo ??
        this.sharedStorageService.get('createMemberRequest')?.idNo ??
        '';
      const name =
        this.sharedStorageService.get('idLoginRequest')?.name ??
        this.sharedStorageService.get('createMemberRequest')?.name ??
        '';
      const userPlanRequest = this.sharedStorageService.get(
        'userPlanRequest'
      ) || {
        orgNo: '',
        gender: '',
        birthday: '',
        age: '0',
        idNo,
        name,
        cell: '',
        email: '',
        telH: '',
        telO: '',
        zipCode: '',
        history: [],
        family: [],
        subjectiveSymptoms: [],
        identityType: '0',
        funcType: '1',
      };
      // 若使用者有登入，取得使用者HIS內健康調查表填寫紀錄
      this.reserveHealthService
        .getQuestionnaireField(userPlanRequest)
        .subscribe(res => {
          this.isLoading = false;
          if (res.statusCode === 'S0000' && res.data) {
            // 暫時儲存 CB-043 的基本資料，稍後會被 SharedStorage 覆蓋
            const cb043PersonalData = res.data.questions.find(
              question => question.questionId === '474'
            ) ?? { questionId: '474', questionAnswers: [] };

            // 檢查是否需要載入 CB-043 資料
            const savedData = this.sharedStorageService.get('savedQuestionnaireData');
            const shouldLoadCB043 = !savedData ||
                                   savedData.caseNo !== this.appointmentInfo?.caseNo ||
                                   !savedData.savedAt;

            if (shouldLoadCB043) {
              // 過濾掉應該在其他 questionId 的 choiceId
              // 1568(骨折詳細)->475, 1569(癌症詳細)->476, 1570(其他慢性病詳細)->477, 1571(其他病史)->478
              const filteredPersonalAnswers = cb043PersonalData.questionAnswers.filter(
                (ans: any) => !['1568', '1569', '1570', '1571'].includes(ans.choiceId)
              );
              this.personalDiseaseCheck = {
                questionId: '474',
                questionAnswers: filteredPersonalAnswers
              };
            }

            // 無論是否載入 CB-043 基本資料，都要載入詳細說明資料
            // 因為詳細說明不會存在 SharedStorage 中，需要從 CB-043 補充

            // 載入其他病史說明 (從 questionId: 478 載入)
            const otherPersonalDiseaseData = res.data.questions.find(
              question => question.questionId === '478'
            );
            if (otherPersonalDiseaseData?.questionAnswers && otherPersonalDiseaseData.questionAnswers.length > 0) {
              this.otherPersonalDisease = otherPersonalDiseaseData.questionAnswers[0].answer;
              console.log('從 CB-043 載入其他病史說明 (questionId 478):', this.otherPersonalDisease);
            }

            // 特別處理癌症詳細說明 (從 questionId: 476 載入)
            const cancerDetailData = res.data.questions.find(
              question => question.questionId === '476'
            );
            if (cancerDetailData?.questionAnswers && cancerDetailData.questionAnswers.length > 0) {
              this.personalDiseaseDetails['1565'] = cancerDetailData.questionAnswers[0].answer;
              console.log('從 CB-043 載入癌症詳細說明 (questionId 476):', cancerDetailData.questionAnswers[0].answer);
            }

            // 特別處理骨折詳細說明 (從 questionId: 475 載入)
            const fractureDetailData = res.data.questions.find(
              question => question.questionId === '475'
            );
            if (fractureDetailData?.questionAnswers && fractureDetailData.questionAnswers.length > 0) {
              this.personalDiseaseDetails['1564'] = fractureDetailData.questionAnswers[0].answer;
              console.log('從 CB-043 載入骨折詳細說明 (questionId 475):', fractureDetailData.questionAnswers[0].answer);
            }

            // 特別處理其他慢性病詳細說明 (從 questionId: 477 載入)
            const chronicDiseaseDetailData = res.data.questions.find(
              question => question.questionId === '477'
            );
            if (chronicDiseaseDetailData?.questionAnswers && chronicDiseaseDetailData.questionAnswers.length > 0) {
              this.personalDiseaseDetails['1566'] = chronicDiseaseDetailData.questionAnswers[0].answer;
              console.log('從 CB-043 載入其他慢性病詳細說明 (questionId 477):', chronicDiseaseDetailData.questionAnswers[0].answer);
            }

            // 只有在需要載入 CB-043 基本資料時才處理 checkbox 選項
            if (shouldLoadCB043) {
              // 處理其他疾病選項
              this.personalDiseaseCheck.questionAnswers.forEach(disease => {
                HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY.diseaseGroups
                  .flatMap(group => group.diseaseOptions)
                  .forEach(option => {
                    if (disease.choiceId === '1540') {
                      this.personalNoneOfFollowing = true;
                    } else if (disease.choiceId === option.choiceId) {
                      this.onPersonalDiseaseCheckboxChange(true, option);
                    }
                  });
              });
            }

            // 暫時儲存 CB-043 的基本資料,稍後會被 SharedStorage 覆蓋
            const cb043FamilyData = res.data.questions.find(
              question => question.questionId === '483'
            ) ?? { questionId: '483', questionAnswers: [] };

            // 使用之前已經宣告的 savedData 和 shouldLoadCB043 變數

            if (shouldLoadCB043) {
              // 過濾掉 choiceId: 1611 (其他家族病史的輸入內容,應該在 questionId: 484)
              const filteredFamilyAnswers = cb043FamilyData.questionAnswers.filter(
                (ans: any) => ans.choiceId !== '1611'
              );
              this.familyDiseaseCheck = {
                questionId: '483',
                questionAnswers: filteredFamilyAnswers
              };
            }

            // 無論是否載入 CB-043 基本資料，都要載入詳細說明資料
            // 載入其他家族病史說明 (從 questionId: 484 載入)
            const otherFamilyDiseaseData = res.data.questions.find(
              question => question.questionId === '484'
            );
            if (otherFamilyDiseaseData?.questionAnswers && otherFamilyDiseaseData.questionAnswers.length > 0) {
              this.otherFamilyDisease = otherFamilyDiseaseData.questionAnswers[0].answer;
              console.log('從 CB-043 載入其他家族病史說明 (questionId 484):', this.otherFamilyDisease);
            }

            // 只有在需要載入 CB-043 基本資料時才處理 checkbox 選項
            if (shouldLoadCB043) {
              this.familyDiseaseCheck.questionAnswers.forEach(disease => {
                HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY.diseaseGroups
                  .flatMap(group => group.diseaseOptions)
                  .forEach(option => {
                    if (disease.choiceId === '1592') {
                      this.familyNoneOfFollowing = true;
                    } else if (disease.choiceId === option.choiceId) {
                      this.onFamilyDiseaseChange(true, option);
                    }
                  });
              });
            }

            // 暫時儲存 CB-043 的基本資料，稍後會被 SharedStorage 覆蓋
            const cb043SubjectiveData = res.data.questions.find(
              question => question.questionId === '472'
            ) ?? { questionId: '472', questionAnswers: [] };

            // 使用之前已經宣告的 shouldLoadCB043 變數
            if (shouldLoadCB043) {
              // 過濾掉不應該在自覺症狀的 choiceId
              // 1539: 自覺症狀其他輸入 -> 應該在 473
              // 1568: 骨折輸入 -> 應該在 475
              // 1569: 癌症輸入 -> 應該在 476
              // 1570: 其他慢性病輸入 -> 應該在 477
              // 1571: 其他病史輸入 -> 應該在 478
              // 1611: 家族病史其他輸入 -> 應該在 484
              const filteredAnswers = cb043SubjectiveData.questionAnswers.filter(
                ans => !['1539', '1568', '1569', '1570', '1571', '1611'].includes(ans.choiceId)
              );
              this.subjectiveSymptomDiseaseCheck = {
                questionId: '472',
                questionAnswers: filteredAnswers
              };
            }

            // 無論是否載入 CB-043 基本資料，都要載入詳細說明資料
            // 載入其他自覺症狀說明 (從 questionId: 473 載入)
            const otherSubjectiveSymptomData = res.data.questions.find(
              question => question.questionId === '473'
            );
            if (otherSubjectiveSymptomData?.questionAnswers && otherSubjectiveSymptomData.questionAnswers.length > 0) {
              this.otherSubjectiveSymptomDisease = otherSubjectiveSymptomData.questionAnswers[0].answer;
              console.log('從 CB-043 載入其他自覺症狀說明 (questionId 473):', this.otherSubjectiveSymptomDisease);
            }

            // 只有在需要載入 CB-043 基本資料時才處理 checkbox 選項
            if (shouldLoadCB043) {
              this.subjectiveSymptomDiseaseCheck.questionAnswers.forEach(
                disease => {
                  HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS.diseaseGroups
                    .flatMap(group => group.diseaseOptions)
                    .forEach(option => {
                      if (disease.choiceId === '1510') {
                        this.subjectiveSymptomNoneOfFollowing = true;
                      } else if (disease.choiceId === option.choiceId) {
                        this.onSubjectiveSymptomDiseaseChange(true, option);
                      }
                    });
                }
              );
            }

            // 暫存 CB-043 資料，但不立即載入
            console.log('CB-043 API 載入完成，資料:', res.data.questions);

            // 不要在這裡判斷狀態，等資料完全處理並儲存後再判斷
          }
        });
    }

    this.personalQuestionnaire = HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY;
    this.familyQuestionnaire = HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY;
    this.subjectiveSymptomQuestionnaire =
      HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS;
    this.longTermMedicationQuestionnaire =
      HEALTH_QUESTIONNAIRE_FIELDS.LONG_TERM_MEDICATIONS;
    this.lifeStyleHabitQuestionnaire =
      HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS;
    this.exerciseHabitQuestionnaire =
      HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS;
  }

  /**
   * 判斷健調表顯示狀態
   * @param skipDataCheck 是否跳過資料檢查（初始化時使用）
   */
  determineQuestionnaireStatus(skipDataCheck: boolean = false): void {
    // 1. 沒有預約資料 → 無需填寫
    if (!this.appointmentInfo) {
      this.questionnaireStatus = 'NOT_NEEDED';
      this.reserveHealthService.updateQuestionnaireStatus('NOT_NEEDED');
      return;
    }

    // 2. 計算距離健檢的天數
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 重置時間部分，只比較日期

    const bookDate = this.parseBookDate(this.appointmentInfo.bookDate);
    bookDate.setHours(0, 0, 0, 0); // 重置時間部分

    const daysUntil = Math.ceil((bookDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    const isBookDatePassed = today > bookDate;

    console.log('健檢日期判斷:', {
      today: today.toISOString(),
      bookDate: bookDate.toISOString(),
      daysUntil,
      isBookDatePassed,
      skipDataCheck
    });

    // 3. 已預約但未報到且健檢日已過 → 逾期狀態
    if (this.appointmentInfo.bookStatus === '1' && isBookDatePassed) {
      this.questionnaireStatus = 'OVERDUE';
      this.reserveHealthService.updateQuestionnaireStatus('OVERDUE');
      return;
    }

    // 4. 預約狀態不是"1"(正常) → 無需填寫
    // bookStatus: "1"=正常, "3"=已報到, "8"=已列印報告, "9"=已寄發報告
    if (this.appointmentInfo.bookStatus !== '1') {
      this.questionnaireStatus = 'NOT_NEEDED';
      this.reserveHealthService.updateQuestionnaireStatus('NOT_NEEDED');
      return;
    }

    // 5. 距離健檢 > 7天 → 等待開放
    if (daysUntil > 7) {
      this.questionnaireStatus = 'WAITING';
      this.reserveHealthService.updateQuestionnaireStatus('WAITING');
      return;
    }

    // 6. 距離健檢 ≤ 7天，檢查是否已經填寫過問卷
    // 初始化時跳過資料檢查，等資料載入完成後再判斷
    if (!skipDataCheck && this.hasQuestionnaireData()) {
      this.questionnaireStatus = 'COMPLETED';
      this.reserveHealthService.updateQuestionnaireStatus('COMPLETED');
      return;
    }

    // 7. 距離健檢 ≤ 7天且未填寫 → 可以填寫
    this.questionnaireStatus = 'AVAILABLE';
    this.reserveHealthService.updateQuestionnaireStatus('AVAILABLE');
  }


  /**
   * 載入資料後重新判斷問卷狀態
   */
  private determineQuestionnaireStatusAfterLoad(): void {
    // 資料載入完成後，進行完整的狀態判斷（包含資料檢查）
    this.determineQuestionnaireStatus(false);
  }

  /**
   * 將問卷標記為已完成（CB-043 載入資料後使用）
   */
  private markQuestionnaireAsCompleted(): void {
    if (!this.appointmentInfo?.caseNo) {
      console.log('沒有 caseNo，無法標記為已完成');
      return;
    }

    // 將 CB-043 載入的資料儲存到 SharedStorage，並設定 savedAt
    const questionnaireData = {
      caseNo: this.appointmentInfo.caseNo,
      questions: this.collectAllQuestions(),
      savedAt: new Date().toISOString()
    };

    this.sharedStorageService.set('savedQuestionnaireData', questionnaireData as any);
    console.log('CB-043 資料已標記為已完成:', questionnaireData);
  }

  /**
   * 檢查是否已經有**完整儲存過**的問卷資料
   * 只有點過「儲存」按鈕才算完成
   */
  private hasQuestionnaireData(): boolean {
    // 從 SharedStorage 檢查是否有完整儲存過的資料
    const savedData = this.sharedStorageService.get('savedQuestionnaireData');

    console.log('檢查問卷是否已完成:', {
      hasSavedData: !!savedData,
      hasSavedAt: !!savedData?.savedAt,
      caseNo: savedData?.caseNo,
      currentCaseNo: this.appointmentInfo?.caseNo,
      questionsCount: savedData?.questions?.length || 0
    });

    // 沒有 savedAt = 沒有點過「儲存」= 未完成
    if (!savedData?.savedAt) {
      console.log('問卷檢查：沒有 savedAt，未完成');
      return false;
    }

    // 檢查是否為同一個案件
    if (savedData.caseNo !== this.appointmentInfo?.caseNo) {
      console.log('問卷檢查：caseNo 不匹配，未完成');
      return false;
    }

    // 檢查問卷是否真正完整（8頁都填寫完成）
    const isComplete = this.checkIfQuestionnaireIsComplete(savedData);

    if (!isComplete) {
      console.log('問卷檢查：未完成所有8頁，未完成');
      return false;
    }

    console.log('問卷檢查：完整問卷（8頁都完成），已完成');
    return true;
  }

  /**
   * 檢查問卷是否真正完整（基於問題數量和關鍵頁面）
   * @param savedData SharedStorage 中的問卷資料
   * @returns 是否完整
   */
  private checkIfQuestionnaireIsComplete(savedData: any): boolean {
    if (!savedData?.questions || !Array.isArray(savedData.questions)) {
      return false;
    }

    const questions = savedData.questions;
    const questionCount = questions.length;

    // 檢查所有8頁是否都有填寫（除了作業經歷為可選）
    const hasPersonalHistory = questions.some((q: any) => q.questionId === '474');      // 步驟2 - 個人病史

    // 家族病史：檢查 questionId 483 或 484（其他家族病史）
    const hasFamilyHistory = questions.some((q: any) => q.questionId === '483') ||
                            questions.some((q: any) => q.questionId === '484');

    // 自覺症狀：檢查 questionId 472 或 473（其他自覺症狀）
    const hasSubjectiveSymptoms = questions.some((q: any) => q.questionId === '472') ||
                                 questions.some((q: any) => q.questionId === '473');

    const hasLongTermMedication = questions.some((q: any) => q.questionId === '481');   // 步驟5 - 長期藥物
    const hasLifestyle = questions.some((q: any) => q.questionId === '485');            // 步驟6 - 生活習慣

    // 檢查基本資料頁面（步驟8 - 其他資訊）
    const hasBasicInfo = questions.some((q: any) =>
      ['511', '513', '514', '515'].includes(q.questionId)
    );

    // 檢查作業經歷頁面（步驟7 - 作業經歷，可選）
    const hasWorkExperience = questions.some((q: any) =>
      ['519', '523'].includes(q.questionId)
    );

    // 1. 包含所有必填頁面：個人病史、家族病史、自覺症狀、長期藥物、生活習慣、基本資料
    // 2. 作業經歷為完全可選
    const hasAllRequiredPages = hasPersonalHistory && hasFamilyHistory && hasSubjectiveSymptoms &&
                               hasLongTermMedication && hasLifestyle && hasBasicInfo;

    const isComplete = hasAllRequiredPages;

    console.log('問卷完整性檢查:', {
      questionCount,
      hasPersonalHistory,
      hasFamilyHistory,
      hasSubjectiveSymptoms,
      hasLongTermMedication,
      hasLifestyle,
      hasBasicInfo,
      hasWorkExperience,
      hasAllRequiredPages,
      isComplete
    });

    return isComplete;
  }

  /**
   * 將 bookDate 字串轉換為 Date 物件
   * @param bookDate "20250101" 格式的日期字串
   * @returns Date 物件
   */
  parseBookDate(bookDate: string): Date {
    const year = parseInt(bookDate.substring(0, 4));
    const month = parseInt(bookDate.substring(4, 6)) - 1; // 月份要減1
    const day = parseInt(bookDate.substring(6, 8));
    return new Date(year, month, day);
  }

//   getAppointmentInfo(): void {
//   // 呼叫 CB-007 API 取得預約資訊
//   this.reserveHealthService.getAppointmentInfo().subscribe({
//     next: (response) => {
//       if (response.statusCode === 'S0000' && response.data) {
//         this.appointmentInfo = response.data;
//         this.determineQuestionnaireStatus();
//       } else {
//         console.warn('取得預約資訊失敗:', response);
//         this.questionnaireStatus = 'NOT_NEEDED';
//       }
//     },
//     error: (error) => {
//       console.error('取得預約資訊發生錯誤:', error);
//       this.questionnaireStatus = 'NOT_NEEDED';
//     }
//   });
// }

  /**
   * 取得預約資訊（暫時用假資料測試）
   */
  // private async loadAppointmentInfo(): Promise<void> {
  //   try {
      // TODO: 呼叫 CB-007 API
      // 先用假資料測試不同狀態
      
      // 測試狀態1：無需填寫（沒有預約）
      // this.appointmentInfo = undefined;
      
      // 測試狀態2：等待開放（距離健檢 > 7天）
      // this.appointmentInfo = {
      //   bookDate: '20250905',
      //   bookStatus: '1'
      // };
      
      // 測試狀態3：可以填寫（距離健檢 ≤ 7天）
      // this.appointmentInfo = {
      //   bookDate: '20251002',
      //   bookStatus: '1'
      // };
      
      // 判斷狀態
  //     this.determineQuestionnaireStatus();
      
  //   } catch (error) {
  //     console.error('取得預約資訊失敗:', error);
  //     this.questionnaireStatus = 'NOT_NEEDED';
  //   }
  // }

  // TODO: 從 SharedStorage 取得 appointmentInfo
  /**
   * 從 SharedStorage 取得預約資訊（從 myReservation 讀取）
   */
  private loadAppointmentInfoFromSharedStorage(): void {
    try {
      // 讀取 'myReservation'
      const myReservation: any = this.sharedStorageService.get('myReservation');

      console.log('從 SharedStorage 取得 myReservation:', myReservation);

      if (myReservation?.bookDate && myReservation?.bookStatus) {
        this.appointmentInfo = {
          bookDate: myReservation.bookDate,
          bookStatus: myReservation.bookStatus,
          caseNo: myReservation.caseNo
        };

        console.log('從 myReservation 建構 appointmentInfo:', this.appointmentInfo);

        this.recordAppointmentStatus();

        // 如果還沒有預先設定過狀態，才進行初步狀態判斷
        if (!this.hasPresetStatus) {
          console.log('尚未預先設定狀態，進行初步狀態判斷');
          // 載入預約資訊後，先進行初步狀態判斷（跳過資料檢查）
          this.determineQuestionnaireStatus(true);
        } else {
          console.log('已經預先設定過狀態，跳過初步狀態判斷');
        }

        // 嘗試從 SharedStorage 載入完整資料
        this.tryLoadFromSharedStorage();

      } else {
        console.log('myReservation.bookDate:', myReservation?.bookDate);
        console.log('myReservation.bookStatus:', myReservation?.bookStatus);
        console.log('myReservation.caseNo:', myReservation?.caseNo);

        // 如果沒有資料，可能是資料還在載入中，延遲一下再檢查
        if (!myReservation) {
          console.log('SharedStorage 中沒有 myReservation，可能還在載入中，延遲重試...');
          setTimeout(() => {
            this.loadAppointmentInfoFromSharedStorage();
          }, 500);
          return;
        }

        // 如果有 myReservation 但缺少必要欄位，才設為 NOT_NEEDED
        this.questionnaireStatus = 'NOT_NEEDED';
        this.reserveHealthService.updateQuestionnaireStatus('NOT_NEEDED');
      }

    } catch (error) {
      console.error('從 SharedStorage 取得 myReservation 失敗:', error);
      // 發生錯誤時也延遲重試一次
      setTimeout(() => {
        this.loadAppointmentInfoFromSharedStorage();
      }, 500);
    }
  }

  /**
   * 記錄預約狀態
   */
  private recordAppointmentStatus(): void {
    if (!this.appointmentInfo) return;

    const today = new Date();
    const bookDate = this.parseBookDate(this.appointmentInfo.bookDate);
    const isBookDatePassed = today > bookDate;
    
    const appointmentStatus = {
      bookStatus: this.appointmentInfo.bookStatus,
      bookDate: this.appointmentInfo.bookDate,
      caseNo: this.appointmentInfo.caseNo,
      isBookDatePassed,
      statusDescription: this.getBookStatusDescription(this.appointmentInfo.bookStatus),
      // 預約狀態判斷
      isBooked: ['1', '3', '8', '9'].includes(this.appointmentInfo.bookStatus),
      isCheckedIn: ['3', '8', '9'].includes(this.appointmentInfo.bookStatus),
      isReportGenerated: ['8', '9'].includes(this.appointmentInfo.bookStatus),
      isReportSent: this.appointmentInfo.bookStatus === '9',
      // Note: 已預約但未報到且健檢日已過
      isOverdueAndNotCheckedIn: this.appointmentInfo.bookStatus === '1' && isBookDatePassed
    };

    // 存入 SharedStorage
    (this.sharedStorageService as {
      set(key: string, value: AppointmentStatus): void;
    }).set('appointmentStatus', appointmentStatus);

    console.log('記錄預約狀態:', appointmentStatus);

    // appointmentInfo 準備好後，嘗試從 SharedStorage 載入完整資料
    this.tryLoadFromSharedStorage();
  }

  /**
   * 嘗試從 SharedStorage 載入完整資料
   */
  private tryLoadFromSharedStorage(): void {
    console.log('嘗試從 SharedStorage 載入完整資料...');

    // 檢查是否已載入欄位定義
    if (!this.personalQuestionnaire || !this.familyQuestionnaire || !this.subjectiveSymptomQuestionnaire) {
      // console.log('欄位定義尚未載入完成，延遲載入 SharedStorage 資料');
      // 延遲 100ms 後重試
      setTimeout(() => {
        this.tryLoadFromSharedStorage();
      }, 100);
      return;
    }

    const loadedFromStorage = this.loadQuestionnaireDataFromStorage();

    if (loadedFromStorage) {
      console.log('成功從 SharedStorage 載入完整資料');
    } else {
      console.log('SharedStorage 中沒有可用資料');
      // 如果 SharedStorage 沒有資料，嘗試使用 CB-043 的資料作為 fallback
      this.loadCB043DataAsFallback();
    }

    // 如果已經預先設定過狀態，不要在這裡重新判斷，等 CB-043 資料完全載入後再判斷
    if (!this.hasPresetStatus) {
      console.log('SharedStorage 載入完成，重新判斷狀態（未預設狀態）');
      this.determineQuestionnaireStatusAfterLoad();
    } else {
      console.log('SharedStorage 載入完成，已預設狀態，跳過重新判斷，等待 CB-043 完成');
    }
  }

  /**
   * 使用 CB-043 資料作為 fallback
   */
  private loadCB043DataAsFallback(): void {
    console.log('使用 CB-043 資料作為 fallback...');

    if (!this.appointmentInfo?.caseNo) {
      console.log('沒有 caseNo，無法載入 CB-043 資料');
      return;
    }

    // 從 SharedStorage 取得使用者資訊
    const idLoginRequest = this.sharedStorageService.get('idLoginRequest');
    const createMemberRequest = this.sharedStorageService.get('createMemberRequest');

    const idNo = idLoginRequest?.idNo ?? createMemberRequest?.idNo ?? '';
    const name = idLoginRequest?.name ?? createMemberRequest?.name ?? '';

    console.log('CB-043 API 呼叫參數:', {
      idLoginRequest,
      createMemberRequest,
      idNo,
      name,
      caseNo: this.appointmentInfo?.caseNo
    });

    if (!idNo) {
      console.error('無法取得 idNo，無法呼叫 CB-043 API');
      return;
    }

    const userPlanRequest: UserPlanRequest = {
      orgNo: '',
      gender: '',
      birthday: '',
      age: '0',
      idNo,
      name,
      cell: '',
      email: '',
      telH: '',
      telO: '',
      zipCode: '',
      history: [],
      family: [],
      subjectiveSymptoms: [],
      identityType: '0',
      funcType: '1',
    };

    this.isLoading = true;
    this.reserveHealthService
      .getQuestionnaireField(userPlanRequest)
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          console.log('CB-043 API 回應:', res);

          if (res.statusCode === 'S0000' && res.data) {
            console.log('CB-043 fallback 資料載入成功:', {
              questionsCount: res.data.questions?.length || 0,
              questions: res.data.questions
            });

            if (res.data.questions && res.data.questions.length > 0) {
              // 載入所有頁面的資料
              this.loadAllPagesFromCB043(res.data.questions);

              // CB-043 有資料表示之前已經填寫過，設定為已完成狀態
              this.markQuestionnaireAsCompleted();

              // 強制觸發 Angular 變更檢測
              this.cdr.detectChanges();

              // 資料完全載入並儲存後，進行最終狀態判斷
              this.determineQuestionnaireStatusAfterLoad();

              console.log('CB-043 資料載入完成，頁面應該已更新');
            } else {
              console.log('CB-043 回傳的 questions 陣列為空，沒有資料可載入');
            }
          } else {
            console.log('CB-043 API 回應失敗或沒有資料:', {
              statusCode: res.statusCode,
              hasData: !!res.data,
              data: res.data
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('CB-043 fallback 載入失敗:', error);
        }
      });
  }

  /**
   * 從 CB-043 載入所有頁面的資料
   */
  private loadAllPagesFromCB043(questions: any[]): void {
    console.log('開始載入所有頁面的 CB-043 資料...');

    // 1. 載入個人病史頁面 (第2頁)
    this.loadPersonalHistoryFromCB043(questions);

    // 2. 載入家族病史頁面 (第3頁)
    this.loadFamilyHistoryFromCB043(questions);

    // 3. 載入自覺症狀頁面 (第4頁)
    this.loadSubjectiveSymptomsFromCB043(questions);

    // 4. 載入長期藥物頁面 (第5頁)
    this.loadLongTermMedicationFromCB043(questions);

    // 5. 載入生活習慣頁面 (第6頁)
    this.loadLifestyleFromCB043(questions);

    // 6. 載入手術史頁面 (第7頁)
    this.loadSurgeryHistoryFromCB043(questions);

    // 7. 載入作業經歷頁面 (第8頁)
    this.loadOccupationFromCB043(questions);

    // 8. 載入其他資訊頁面 (第9頁)
    this.loadOtherInfoFromCB043(questions);

    console.log('所有頁面的 CB-043 資料載入完成');

    // 除錯：檢查載入後的資料狀態
    this.logLoadedDataStatus();
  }

  /**
   * 除錯：記錄載入後的資料狀態
   */
  private logLoadedDataStatus(): void {
    console.log('=== CB-043 載入後的資料狀態 ===');
    console.log('個人病史選項數量:', this.personalDiseaseCheck.questionAnswers?.length || 0);
    console.log('家族病史選項數量:', this.familyDiseaseCheck.questionAnswers?.length || 0);
    console.log('自覺症狀選項數量:', this.subjectiveSymptomDiseaseCheck.questionAnswers?.length || 0);
    console.log('長期藥物選項數量:', this.longTermMedications?.length || 0);
    console.log('生活習慣選項數量:', this.lifeStyleHabits?.length || 0);
    console.log('手術史狀態:', this.hasSurgeryHistoryOrMajorTrauma);
    console.log('運動習慣狀態:', this.hasExerciseHabit);
    console.log('目前職業表單值:', this.occupation.value);
    console.log('過去職業表單值:', this.previousOccupation.value);
    console.log('================================');
  }

  /**
   * 從 CB-043 載入個人病史資料 (第2頁)
   */
  private loadPersonalHistoryFromCB043(questions: any[]): void {
    // 載入個人病史選項 (questionId: 474)
    const personalHistoryData = questions.find(q => q.questionId === '474');
    if (personalHistoryData?.questionAnswers?.length > 0) {
      // 過濾掉應該在其他 questionId 的 choiceId
      const filteredAnswers = personalHistoryData.questionAnswers.filter(
        (ans: any) => !['1568', '1569', '1570', '1571'].includes(ans.choiceId)
      );

      this.personalDiseaseCheck.questionAnswers = filteredAnswers;

      // 處理其他疾病選項
      this.personalDiseaseCheck.questionAnswers.forEach(disease => {
        HEALTH_QUESTIONNAIRE_FIELDS.PERSONAL_HISTORY.diseaseGroups
          .flatMap(group => group.diseaseOptions)
          .forEach(option => {
            if (option.choiceId === disease.choiceId) {
              // 將疾病加入到 personalDiseases 陣列中
              if (!this.personalDiseases.includes(option.choiceId)) {
                this.personalDiseases.push(option.choiceId);
              }
            }
          });
      });
    }

    // 載入其他病史說明 (questionId: 478)
    const otherPersonalDiseaseData = questions.find(q => q.questionId === '478');
    if (otherPersonalDiseaseData?.questionAnswers?.length > 0) {
      this.otherPersonalDisease = otherPersonalDiseaseData.questionAnswers[0].answer;
    }

    // 載入癌症詳細說明 (questionId: 476)
    const cancerDetailData = questions.find(q => q.questionId === '476');
    if (cancerDetailData?.questionAnswers?.length > 0) {
      this.personalDiseaseDetails['1565'] = cancerDetailData.questionAnswers[0].answer;
    }

    // 載入骨折詳細說明 (questionId: 475)
    const fractureDetailData = questions.find(q => q.questionId === '475');
    if (fractureDetailData?.questionAnswers?.length > 0) {
      this.personalDiseaseDetails['1564'] = fractureDetailData.questionAnswers[0].answer;
    }

    // 載入其他慢性病詳細說明 (questionId: 477)
    const chronicDiseaseDetailData = questions.find(q => q.questionId === '477');
    if (chronicDiseaseDetailData?.questionAnswers?.length > 0) {
      this.personalDiseaseDetails['1566'] = chronicDiseaseDetailData.questionAnswers[0].answer;
    }

    console.log('個人病史資料載入完成');
  }

  /**
   * 從 CB-043 載入家族病史資料 (第3頁)
   */
  private loadFamilyHistoryFromCB043(questions: any[]): void {
    // 載入家族病史選項 (questionId: 483)
    const familyHistoryData = questions.find(q => q.questionId === '483');
    if (familyHistoryData?.questionAnswers?.length > 0) {
      // 過濾掉 choiceId: 1611 (其他家族病史的輸入內容)
      const filteredAnswers = familyHistoryData.questionAnswers.filter(
        (ans: any) => ans.choiceId !== '1611'
      );

      this.familyDiseaseCheck.questionAnswers = filteredAnswers;

      // 處理家族病史選項
      this.familyDiseaseCheck.questionAnswers.forEach(disease => {
        HEALTH_QUESTIONNAIRE_FIELDS.FAMILY_HISTORY.diseaseGroups
          .flatMap(group => group.diseaseOptions)
          .forEach(option => {
            if (option.choiceId === disease.choiceId) {
              // 將疾病加入到 familyDiseases 陣列中
              if (!this.familyDiseases.includes(option.choiceId)) {
                this.familyDiseases.push(option.choiceId);
              }
            }
          });
      });
    }

    // 載入其他家族病史說明 (questionId: 484)
    const otherFamilyDiseaseData = questions.find(q => q.questionId === '484');
    if (otherFamilyDiseaseData?.questionAnswers?.length > 0) {
      this.otherFamilyDisease = otherFamilyDiseaseData.questionAnswers[0].answer;
    }

    console.log('家族病史資料載入完成');
  }

  /**
   * 從 CB-043 載入自覺症狀資料 (第4頁)
   */
  private loadSubjectiveSymptomsFromCB043(questions: any[]): void {
    // 載入自覺症狀選項 (questionId: 472)
    const subjectiveData = questions.find(q => q.questionId === '472');
    if (subjectiveData?.questionAnswers?.length > 0) {
      // 過濾掉不應該在自覺症狀的 choiceId
      const filteredAnswers = subjectiveData.questionAnswers.filter(
        (ans: any) => !['1539', '1568', '1569', '1570', '1571'].includes(ans.choiceId)
      );

      this.subjectiveSymptomDiseaseCheck.questionAnswers = filteredAnswers;

      // 處理自覺症狀選項
      this.subjectiveSymptomDiseaseCheck.questionAnswers.forEach(disease => {
        HEALTH_QUESTIONNAIRE_FIELDS.SUBJECTIVE_SYMPTOMS.diseaseGroups
          .flatMap(group => group.diseaseOptions)
          .forEach(option => {
            if (option.choiceId === disease.choiceId) {
              // 將症狀加入到 subjectiveSymptomDiseases 陣列中
              if (!this.subjectiveSymptomDiseases.includes(option.choiceId)) {
                this.subjectiveSymptomDiseases.push(option.choiceId);
              }
            }
          });
      });
    }

    // 載入其他自覺症狀說明 (questionId: 473)
    const otherSubjectiveData = questions.find(q => q.questionId === '473');
    if (otherSubjectiveData?.questionAnswers?.length > 0) {
      this.otherSubjectiveSymptomDisease = otherSubjectiveData.questionAnswers[0].answer;
    }

    console.log('自覺症狀資料載入完成');
  }

  /**
   * 從 CB-043 載入長期藥物資料 (第5頁)
   */
  private loadLongTermMedicationFromCB043(questions: any[]): void {
    // 載入長期藥物選項 (questionId: 481)
    const medicationData = questions.find(q => q.questionId === '481');
    if (medicationData?.questionAnswers?.length > 0) {
      medicationData.questionAnswers.forEach((answer: any) => {
        // 檢查是否為「以下皆無」
        if (answer.choiceId === '1575' && answer.answer === '1') {
          this.longTermMedicationNoneOfFollowing = true;
          // 當選擇「以下皆無」時，也要加入到 longTermMedications 陣列和 questionAnswers 中
          this.longTermMedications = [NONE_OF_FOLLOWING];
          this.longTermMedicationCheck.questionAnswers = [{
            choiceId: answer.choiceId,
            answer: answer.answer
          }];
        } else {
          // 尋找其他藥物選項
          const medicationOption = HEALTH_QUESTIONNAIRE_FIELDS.LONG_TERM_MEDICATIONS.diseaseGroups
            .flatMap(group => group.diseaseOptions)
            .find(opt => opt.choiceId === answer.choiceId);

          if (medicationOption) {
            // 加入到長期藥物檢查中
            this.longTermMedicationCheck.questionAnswers.push({
              choiceId: medicationOption.choiceId,
              answer: medicationOption.answer
            });
            // 加入到長期藥物陣列
            this.longTermMedications.push(medicationOption.name);
          }
        }
      });
    }

    // 載入其他長期藥物說明 (questionId: 482)
    const otherMedicationData = questions.find(q => q.questionId === '482');
    if (otherMedicationData?.questionAnswers?.length > 0) {
      this.otherLongTermMedication = otherMedicationData.questionAnswers[0].answer;
    }

    console.log('長期藥物資料載入完成');
  }

  /**
   * 從 CB-043 載入生活習慣資料 (第6頁)
   */
  private loadLifestyleFromCB043(questions: any[]): void {
    // 檳榔 (questionId: 485)
    const betelNutQuestion = questions.find(q => q.questionId === '485');
    if (betelNutQuestion?.questionAnswers?.length > 0) {
      const answer = betelNutQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const betelOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (betelOption) {
        this.selectedLifeStyleHabits[0] = betelOption.name;
      }
    }

    // 檳榔詳細資料 (questionId: 486, 487, 488, 489)
    const betelNutDetailQuestions = questions.filter(q =>
      ['486', '487', '488', '489'].includes(q.questionId)
    );
    betelNutDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '486': // 每天顆數
            this.betelNutPerDay.setValue(parseInt(answer) || 0);
            break;
          case '487': // 嚼食年數
            this.betelNutYears.setValue(parseInt(answer) || 0);
            break;
          case '488': // 戒食年數
            this.betelNutQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '489': // 戒食月數
            this.betelNutQuitMonths.setValue(parseInt(answer) || 0);
            break;
        }
      }
    });

    // 吸菸 (questionId: 490)
    const smokingQuestion = questions.find(q => q.questionId === '490');
    if (smokingQuestion?.questionAnswers?.length > 0) {
      const answer = smokingQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const smokingOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[1].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (smokingOption) {
        this.selectedLifeStyleHabits[1] = smokingOption.name;
      }
    }

    // 吸菸詳細資料 (questionId: 491, 492, 493, 494)
    const smokingDetailQuestions = questions.filter(q =>
      ['491', '492', '493', '494'].includes(q.questionId)
    );
    smokingDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '491': // 每天支數
            this.cigarettesPerDay.setValue(parseInt(answer) || 0);
            break;
          case '492': // 吸菸年數
            this.smokingYears.setValue(parseInt(answer) || 0);
            break;
          case '493': // 戒菸年數
            this.smokingQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '494': // 戒菸月數
            this.smokingQuitMonths.setValue(parseInt(answer) || 0);
            break;
        }
      }
    });

    // 飲酒 (questionId: 495)
    const alcoholQuestion = questions.find(q => q.questionId === '495');
    if (alcoholQuestion?.questionAnswers?.length > 0) {
      const answer = alcoholQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const alcoholOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[2].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (alcoholOption) {
        this.selectedLifeStyleHabits[2] = alcoholOption.name;
      }
    }

    // 飲酒詳細資料 (questionId: 496, 497, 498, 499, 500)
    const alcoholDetailQuestions = questions.filter(q =>
      ['496', '497', '498', '499', '500'].includes(q.questionId)
    );
    alcoholDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '496': // 每週次數
            this.alcoholTimesPerWeek.setValue(parseInt(answer) || 0);
            break;
          case '497': // 酒類
            this.alcoholType.setValue(answer);
            break;
          case '498': // 每天cc數
            this.alcoholAmountCc.setValue(parseInt(answer) || 0);
            break;
          case '499': // 戒酒年數
            this.alcoholQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '500': // 戒酒月數
            this.alcoholQuitMonths.setValue(parseInt(answer) || 0);
            break;
        }
      }
    });

    // 飲食習慣 (questionId: 501)
    const dietHabitQuestion = questions.find(q => q.questionId === '501');
    if (dietHabitQuestion?.questionAnswers?.length > 0) {
      // 清空現有的飲食習慣
      this.lifeStyleHabits = this.lifeStyleHabits.filter(habit =>
        !habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType)
      );

      // 載入飲食習慣選項
      dietHabitQuestion.questionAnswers.forEach((answer: any) => {
        const dietOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseOptions
          .find(opt => opt.answer === answer.answer);
        if (dietOption) {
          const habitValue = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType + '_' + dietOption.name;
          this.lifeStyleHabits.push(habitValue);
        }
      });
    }

    // 運動習慣 (questionId: 502) - 是否有運動習慣
    const exerciseHabitQuestion = questions.find(q => q.questionId === '502');
    if (exerciseHabitQuestion?.questionAnswers?.length > 0) {
      const answer = exerciseHabitQuestion.questionAnswers[0];
      // 根據 answer 判斷是否有運動習慣
      const exerciseOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (exerciseOption) {
        this.hasExerciseHabit = exerciseOption.name.includes('無') ? API_PARAMS.NO : API_PARAMS.YES;
      }
    }

    console.log('生活習慣資料載入完成');
  }

  /**
   * 從 CB-043 載入手術史資料 (第7頁)
   */
  private loadSurgeryHistoryFromCB043(questions: any[]): void {
    // 是否有手術史或重大外傷 (questionId: 479)
    const surgeryQuestion = questions.find(q => q.questionId === '479');
    if (surgeryQuestion?.questionAnswers?.length > 0) {
      const answer = surgeryQuestion.questionAnswers[0];
      // answer.answer: '1' = 無, '2' = 有
      this.hasSurgeryHistoryOrMajorTrauma = answer.answer === '2' ? API_PARAMS.YES : API_PARAMS.NO;
    }

    // 手術史或重大外傷詳細說明 (questionId: 480)
    const surgeryDetailQuestion = questions.find(q => q.questionId === '480');
    if (surgeryDetailQuestion?.questionAnswers?.length > 0) {
      this.surgeryHistoryOrMajorTrauma = surgeryDetailQuestion.questionAnswers[0].answer;
    }

    console.log('手術史資料載入完成');
  }

  /**
   * 從 CB-043 載入作業經歷資料 (第8頁)
   */
  private loadOccupationFromCB043(questions: any[]): void {
    // ========== 目前從事職業 ==========

    // 目前從事行業 (questionId: 523)
    const occupationCategoryQuestion = questions.find(q => q.questionId === '523');
    if (occupationCategoryQuestion?.questionAnswers?.length > 0) {
      this.occupation.patchValue({
        category: occupationCategoryQuestion.questionAnswers[0].answer
      });
    }

    // 目前從事行業其他說明 (questionId: 524)
    const occupationOtherQuestion = questions.find(q => q.questionId === '524');
    if (occupationOtherQuestion?.questionAnswers?.length > 0) {
      this.occupation.patchValue({
        otherOccupation: occupationOtherQuestion.questionAnswers[0].answer
      });
    }

    // 受僱日期起 (questionId: 525)
    const employmentDateStartQuestion = questions.find(q => q.questionId === '525');
    if (employmentDateStartQuestion?.questionAnswers?.length > 0) {
      const dateStr = employmentDateStartQuestion.questionAnswers[0].answer;
      const employmentDate = moment(dateStr, 'YYYY/MM');
      if (employmentDate.isValid()) {
        this.occupation.patchValue({
          employmentDate: employmentDate
        });
      }
    }

    // 是否需輪班 (questionId: 527) 和 輪班制 (questionId: 528)
    const needsShiftQuestion = questions.find(q => q.questionId === '527');
    const shiftTypeQuestion = questions.find(q => q.questionId === '528');

    console.log('CB-043 載入輪班資料:', {
      needsShiftQuestion: needsShiftQuestion?.questionAnswers,
      shiftTypeQuestion: shiftTypeQuestion?.questionAnswers
    });

    if (needsShiftQuestion?.questionAnswers?.length > 0) {
      const needsShiftAnswer = needsShiftQuestion.questionAnswers[0].answer;
      console.log('CB-043 檢測到「是否需輪班」資料:', needsShiftAnswer);

      if (needsShiftAnswer === '1') {
        // 選擇「否」，使用特殊值 'needs_shift_no' 避免與輪班制衝突
        console.log('CB-043 用戶選擇「否」，載入為不需輪班');
        this.occupation.patchValue({
          type: 'needs_shift_no'
        });
      } else if (needsShiftAnswer === '2') {
        // 選擇「是」，檢查是否有具體的輪班制選項
        console.log('CB-043 用戶選擇「是」，檢查具體輪班制選項');
        if (shiftTypeQuestion?.questionAnswers?.length > 0) {
          const shiftTypeAnswer = shiftTypeQuestion.questionAnswers[0].answer;
          console.log('CB-043 載入具體輪班制:', shiftTypeAnswer);
          // 使用特殊值前綴避免衝突
          this.occupation.patchValue({
            type: `shift_type_${shiftTypeAnswer}`
          });
        } else {
          // 沒有具體輪班制，使用「是」
          console.log('CB-043 沒有具體輪班制，使用「是」');
          this.occupation.patchValue({
            type: 'needs_shift_yes'
          });
        }
      }
    } else if (shiftTypeQuestion?.questionAnswers?.length > 0) {
      // 只有輪班制選項，沒有「是否需輪班」的記錄（舊資料）
      const shiftTypeAnswer = shiftTypeQuestion.questionAnswers[0].answer;
      console.log('CB-043 只有輪班制選項（舊資料）:', shiftTypeAnswer);
      this.occupation.patchValue({
        type: `shift_type_${shiftTypeAnswer}`
      });
    } else {
      console.log('CB-043 沒有任何輪班相關資料');
    }

    // ========== 曾經從事職業 ==========

    // 曾經從事行業 (questionId: 519)
    const prevOccupationQuestion = questions.find(q => q.questionId === '519');
    if (prevOccupationQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        category: prevOccupationQuestion.questionAnswers[0].answer
      });
    }

    // 曾經從事行業其他說明 (questionId: 520)
    const prevOccupationOtherQuestion = questions.find(q => q.questionId === '520');
    if (prevOccupationOtherQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        otherOccupation: prevOccupationOtherQuestion.questionAnswers[0].answer
      });
    }

    // 開始年月 (questionId: 521)
    const startDateQuestion = questions.find(q => q.questionId === '521');
    if (startDateQuestion?.questionAnswers?.length > 0) {
      const dateStr = startDateQuestion.questionAnswers[0].answer; // 格式: YYYY/MM
      const [year, month] = dateStr.split('/');
      this.previousOccupation.patchValue({
        startYear: year,
        startMonth: String(parseInt(month))
      });
    }

    // 結束年月 (questionId: 522)
    const endDateQuestion = questions.find(q => q.questionId === '522');
    if (endDateQuestion?.questionAnswers?.length > 0) {
      const dateStr = endDateQuestion.questionAnswers[0].answer; // 格式: YYYY/MM
      const [year, month] = dateStr.split('/');
      this.previousOccupation.patchValue({
        endYear: year,
        endMonth: String(parseInt(month))
      });
    }

    // 過去一個月平均工時 (questionId: 530)
    const avgWorkHoursPastMonthQuestion = questions.find(q => q.questionId === '530');
    if (avgWorkHoursPastMonthQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgWorkHoursPastMonth: parseInt(avgWorkHoursPastMonthQuestion.questionAnswers[0].answer) || 0
      });
    }

    // 過去六個月平均工時 (questionId: 531)
    const avgWorkHoursPastSixMonthsQuestion = questions.find(q => q.questionId === '531');
    if (avgWorkHoursPastSixMonthsQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgWorkHoursPastSixMonths: parseInt(avgWorkHoursPastSixMonthsQuestion.questionAnswers[0].answer) || 0
      });
    }

    // 輪班方式說明 (questionId: 529) - 只有在選擇「其他」輪班制時才載入
    const shiftDescriptionQuestion = questions.find(q => q.questionId === '529');
    if (shiftDescriptionQuestion?.questionAnswers?.length > 0) {
      // 檢查當前是否選擇了「其他」輪班制
      const currentType = this.occupation.value.type;
      if (currentType === 'shift_type_4') {
        this.occupation.patchValue({
          shiftDescription: shiftDescriptionQuestion.questionAnswers[0].answer
        });
      }
    }

    // 檢查原因 (questionId: 532)
    const examReasonQuestion = questions.find(q => q.questionId === '532');
    if (examReasonQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        examReason: examReasonQuestion.questionAnswers[0].answer
      });
    }

    // 平均睡眠時間 (questionId: 533)
    const avgSleepTimeQuestion = questions.find(q => q.questionId === '533');
    if (avgSleepTimeQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgSleepTime: parseInt(avgSleepTimeQuestion.questionAnswers[0].answer) || 0
      });
    }

    console.log('作業經歷資料載入完成');
  }

  /**
   * 從 CB-043 載入其他資訊資料 (第9頁)
   */
  private loadOtherInfoFromCB043(questions: any[]): void {
    // 載入運動習慣詳細資料
    this.loadExerciseHabitDetails(questions);

    // 常住國家 (questionId: 511)
    const residenceCountryQuestion = questions.find(q => q.questionId === '511');
    if (residenceCountryQuestion?.questionAnswers?.length > 0) {
      this.residenceCountries = residenceCountryQuestion.questionAnswers.map((answer: any) => answer.answer);
    }

    // 常住國家其他 (questionId: 512)
    const residenceCountryOtherQuestion = questions.find(q => q.questionId === '512');
    if (residenceCountryOtherQuestion?.questionAnswers?.length > 0) {
      this.otherResidenceCountry = residenceCountryOtherQuestion.questionAnswers[0].answer;
    }

    // 婚姻狀況 (questionId: 513)
    const maritalStatusQuestion = questions.find(q => q.questionId === '513');
    if (maritalStatusQuestion?.questionAnswers?.length > 0) {
      this.maritalStatus = maritalStatusQuestion.questionAnswers[0].answer;
    }

    // 教育程度 (questionId: 514)
    const educationLevelQuestion = questions.find(q => q.questionId === '514');
    if (educationLevelQuestion?.questionAnswers?.length > 0) {
      this.educationLevel = educationLevelQuestion.questionAnswers[0].answer;
    }

    // 職位 (questionId: 515)
    const positionQuestion = questions.find(q => q.questionId === '515');
    if (positionQuestion?.questionAnswers?.length > 0) {
      this.position = positionQuestion.questionAnswers[0].answer;
    }

    // 職位其他 (questionId: 516)
    const positionOtherQuestion = questions.find(q => q.questionId === '516');
    if (positionOtherQuestion?.questionAnswers?.length > 0) {
      this.otherPosition = positionOtherQuestion.questionAnswers[0].answer;
    }

    console.log('其他資訊資料載入完成');
  }

  /**
   * 取得預約狀態描述
   */
  private getBookStatusDescription(bookStatus: string): string {
    const statusMap: { [key: string]: string } = {
      '1': '正常',
      '3': '已報到', 
      '8': '已列印報告',
      '9': '已寄發報告'
    };
    return statusMap[bookStatus] || '未知狀態';
  }

  // 切換個人病史"以下皆無"選項
  togglePersonalNoneOfFollowing() {
    // 標記表單已修改
    this.markFormDirty();
    // 如果勾選以下皆無
    if (this.personalNoneOfFollowing) {
      const value =
        this.personalHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.personalDiseases = [value]; // 清空個人病史勾選 只放 "既往病史_以下皆無"
      this.personalDiseaseDetails = {}; // 清空個人病史詳細症狀
      this.otherPersonalDisease = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無個人病史
      if (
        !this.personalDiseaseCheck.questionAnswers.some(
          asw => asw.choiceId === '1540'
        )
      ) {
        this.personalDiseaseCheck.questionAnswers = [
          { choiceId: '1540', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.personalDiseases = [];
      this.personalDiseaseCheck.questionAnswers = [];
    }
    // 清除錯誤狀態
    if (this.personalNoneOfFollowing) {
      this.showPersonalHistoryError = false;
    }
  }

  /**
   * 檢查其他慢性疾病、癌症、骨折 checkbox 是否被勾選
   */
  isPersonalDiseaseChecked(choiceId: string): boolean {
    return this.personalDiseaseCheck.questionAnswers.some(
      answer => answer.choiceId === choiceId
    );
  }

  // 勾選/取消個人病史症狀
  onPersonalDiseaseCheckboxChange(
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    // 標記表單已修改
    this.markFormDirty();
    const value = this.personalHistoryDiseasePipe.transform(diseaseOption.name);
    
    if (diseaseOption.detailPlaceholder) {
      // 如果此欄位是詳細症狀
      if (checked) {
        // 勾選時初始化為空字串
        this.personalDiseaseDetails[diseaseOption.choiceId] = '';

        // 加入到 questionAnswers
        if (!this.personalDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )) {
          this.personalDiseaseCheck.questionAnswers.push({
            choiceId: diseaseOption.choiceId,
            answer: diseaseOption.answer,
          });
        }
      } else {
        // 取消勾選，清空詳細資料和答案
        delete this.personalDiseaseDetails[diseaseOption.choiceId];

        this.personalDiseaseCheck.questionAnswers =
          this.personalDiseaseCheck.questionAnswers.filter(ans => {
            return (
              ans.choiceId !== diseaseOption.choiceId &&
              ans.choiceId !== diseaseOption.detailPlaceholderId
            );
          });
      }
    } else {
      // 一般選項的處理保持不變
      if (checked) {
        this.personalDiseases.push(value);
        if (!this.personalDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )) {
          this.personalDiseaseCheck.questionAnswers.push({
            choiceId: diseaseOption.choiceId,
            answer: diseaseOption.answer,
          });
        }
      } else {
        const idx = this.personalDiseases.indexOf(value);
        if (idx > -1) this.personalDiseases.splice(idx, 1);

        this.personalDiseaseCheck.questionAnswers =
          this.personalDiseaseCheck.questionAnswers.filter(ans => {
            return ans.choiceId !== diseaseOption.choiceId;
          });
      }
    }
    
    // 清除錯誤狀態
    if (checked) {
      this.showPersonalHistoryError = false;
    }
  }

  onPersonalDiseaseDetailInput(diseaseOption: DiseaseOption, event?: Event): void {
    // 標記表單已修改
    this.markFormDirty();

    // 直接從 event 取得輸入值，確保是最新的
    const inputElement = event?.target as HTMLInputElement;
    const detailText = inputElement?.value ?? this.personalDiseaseDetails[diseaseOption.choiceId] ?? '';

    // 更新 personalDiseaseDetails
    this.personalDiseaseDetails[diseaseOption.choiceId] = detailText;

    const checkboxId = diseaseOption.choiceId;
    const checkboxAns = diseaseOption.answer;

    // 簡化的除錯輸出
    console.log(`個人病史詳細輸入: ${diseaseOption.name} = "${detailText}"`);

    // 只確保主選項 (checkbox) 存在於 questionAnswers 中
    const checkboxAnswer = this.personalDiseaseCheck.questionAnswers.find(
      ans => ans.choiceId === checkboxId
    );

    if (!checkboxAnswer) {
      this.personalDiseaseCheck.questionAnswers.push({
        choiceId: checkboxId,
        answer: checkboxAns,
      });
    }

    this.personalNoneOfFollowing = false;
  }

  // 切換家族病史"以下皆無"選項
  toggleFamilyNoneOfFollowing() {
    // 標記表單已修改
    this.markFormDirty();
    if (this.familyNoneOfFollowing) {
      const value = this.familyHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.familyDiseases = [value]; // 清空家族病史勾選
      this.familyDiseaseDetails = {}; // 清空家族病史詳細症狀
      this.otherFamilyDisease = ''; // 清空其他症狀輸入
      
      if (!this.familyDiseaseCheck.questionAnswers.some(
        asw => asw.choiceId === '1592'
      )) {
        this.familyDiseaseCheck.questionAnswers = [
          { choiceId: '1592', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列
      this.familyDiseases = [];
      this.familyDiseaseCheck.questionAnswers = [];
    }
    
    // 清除錯誤狀態
    if (this.familyNoneOfFollowing) {
      this.showFamilyHistoryError = false;
    }
  }

  // 勾選/取消家族病史症狀
  onFamilyDiseaseChange(checked: boolean, diseaseOption: DiseaseOption) {
    // 標記表單已修改
    this.markFormDirty();
    const value = this.familyHistoryDiseasePipe.transform(diseaseOption.name);
    if (checked) {
      this.familyDiseases.push(value);

      if (
        !this.familyDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.familyDiseaseCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
    } else {
      const idx = this.familyDiseases.indexOf(value);
      if (idx > -1) this.familyDiseases.splice(idx, 1);

      this.familyDiseaseCheck.questionAnswers =
        this.familyDiseaseCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
    // 清除錯誤狀態
    if (checked) {
      this.showFamilyHistoryError = false;
    }
  }

  /**
   * 其他家族疾病輸入時的處理
   */
  onOtherFamilyDiseaseInput(event: Event): void {
    this.markFormDirty();
    const inputValue = (event.target as HTMLInputElement).value;
    this.otherFamilyDisease = inputValue;

    // 如果有填寫內容，清除錯誤狀態
    if (inputValue?.trim()) {
      this.showFamilyHistoryError = false;
    }
  }

  /**
   * 其他自覺症狀輸入時的處理
   */
  onOtherSubjectiveSymptomInput(event: Event): void {
    this.markFormDirty();
    const inputValue = (event.target as HTMLInputElement).value;
    this.otherSubjectiveSymptomDisease = inputValue;

    // 如果有填寫內容，清除錯誤狀態
    if (inputValue?.trim()) {
      this.showSubjectiveSymptomError = false;
    }
  }

  // 切換自覺症狀"以下皆無"選項
  toggleSubjectiveSymptomNoneOfFollowing() {
    // 標記表單已修改
    this.markFormDirty();
    if (this.subjectiveSymptomNoneOfFollowing) {
      const value =
        this.subjectiveSymptomDiseasePipe.transform(NONE_OF_FOLLOWING);
      this.subjectiveSymptomDiseases = [value]; // 清空自覺症狀勾選 只放 "自覺症狀_以下皆無"
      this.otherSubjectiveSymptomDisease = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無自覺症狀
      if (
        !this.subjectiveSymptomDiseaseCheck.questionAnswers.some(
          asw => asw.choiceId === '1510'
        )
      ) {
        this.subjectiveSymptomDiseaseCheck.questionAnswers = [
          { choiceId: '1510', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.subjectiveSymptomDiseases = [];
      this.subjectiveSymptomDiseaseCheck.questionAnswers = [];
    }
    // 清除錯誤狀態
    if (this.subjectiveSymptomNoneOfFollowing) {
      this.showSubjectiveSymptomError = false;
    }
  }

  // 勾選/取消自覺症狀
  onSubjectiveSymptomDiseaseChange(
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    // 標記表單已修改
    this.markFormDirty();
    const value = this.subjectiveSymptomDiseasePipe.transform(
      diseaseOption.name
    );
    if (checked) {
      this.subjectiveSymptomDiseases.push(value);

      if (
        !this.subjectiveSymptomDiseaseCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.subjectiveSymptomDiseaseCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
    } else {
      const idx = this.subjectiveSymptomDiseases.indexOf(value);
      if (idx > -1) this.subjectiveSymptomDiseases.splice(idx, 1);

      this.subjectiveSymptomDiseaseCheck.questionAnswers =
        this.subjectiveSymptomDiseaseCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
    // 清除錯誤狀態
    if (checked) {
      this.showSubjectiveSymptomError = false;
    }
  }

  /**
   * 其他長期藥物輸入時的處理
   */
  onOtherLongTermMedicationInput(event: Event): void {
    this.markFormDirty();
    const inputValue = (event.target as HTMLInputElement).value;
    this.otherLongTermMedication = inputValue;

    // 如果有填寫內容，清除錯誤狀態
    if (inputValue?.trim()) {
      this.showLongTermMedicationError = false;
    }
  }

  // 切換長期服用藥物"以下皆無"選項
  toggleLongTermMedicationNoneOfFollowing() {
    // 標記表單已修改
    this.markFormDirty();
    if (this.longTermMedicationNoneOfFollowing) {
      this.hasOtherLongTermMedication = false;
      this.longTermMedications = [NONE_OF_FOLLOWING]; // 清空自覺症狀勾選 只放 "自覺症狀_以下皆無"
      this.otherLongTermMedication = ''; // 清空其他症狀輸入

      // 如果勾選以下皆無 陣列終將只會有此ID代表無自覺症狀
      if (
        !this.longTermMedicationCheck.questionAnswers.some(
          asw => asw.choiceId === '1575'
        )
      ) {
        this.longTermMedicationCheck.questionAnswers = [
          { choiceId: '1575', answer: '1' },
        ];
      }
    } else {
      // 如果取消勾選 清空陣列 後續勾選其他項目會再放入
      this.longTermMedications = [];
      this.longTermMedicationCheck.questionAnswers = [];
    }

    if (this.longTermMedicationNoneOfFollowing) {
      this.showLongTermMedicationError = false;
    }
  }

  // 勾選/取消長期服用藥物
  onLongTermMedicationChange(checked: boolean, diseaseOption: DiseaseOption) {
    // 標記表單已修改
    this.markFormDirty();
    const value = diseaseOption.name;

    if (checked) {
      this.longTermMedications.push(value);

      if (
        !this.longTermMedicationCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.longTermMedicationCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
    } else {
      const idx = this.longTermMedications.indexOf(value);
      if (idx > -1) this.longTermMedications.splice(idx, 1);

      this.longTermMedicationCheck.questionAnswers =
        this.longTermMedicationCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
    
    if (checked) {
      this.showLongTermMedicationError = false;
    }
  }

  // 切換是否有手術史或重大外傷
  toggleHasSurgeryHistoryOrMajorTrauma() {
    // 標記表單已修改
    this.markFormDirty();
    if (this.hasSurgeryHistoryOrMajorTrauma === API_PARAMS.NO) {
      this.surgeryHistoryOrMajorTrauma = ''; // 清空手術史或重大外傷名稱
    }
  }

  onInputHasSurgeryHistoryOrMajorTrauma(): void {
    // 標記表單已修改
    this.markFormDirty();
    if (this.hasSurgeryHistoryOrMajorTrauma !== this.API_PARAMS.YES) {
      this.hasSurgeryHistoryOrMajorTrauma = this.API_PARAMS.YES; // 輸入時自動選 YES
    }
  }

  // 修改生活習慣驗證方法，返回各題的驗證結果
  isLifeStyleQuestionValid(index: number): boolean {
    if (index < 3) {
      // Radio button questions (0, 1, 2)
      return this.selectedLifeStyleHabits[index] !== undefined;
    } else if (index === 3) {
      // Checkbox question (3)
      return this.lifeStyleHabits.some(habit => 
        habit.startsWith(this.lifeStyleHabitQuestionnaire?.diseaseGroups[3]?.diseaseType ?? '')
      );
    }
    return true;
  }

  // 檢查生活習慣 radio button 是否都有選擇
  isLifeStyleRadioValid(): boolean {
    return (
      this.selectedLifeStyleHabits[0] !== undefined &&
      this.selectedLifeStyleHabits[1] !== undefined &&
      this.selectedLifeStyleHabits[2] !== undefined
    );
  }

  // 檢查生活習慣 checkbox 是否有選擇
  isLifeStyleCheckboxValid(): boolean {
    return this.lifeStyleHabits.some(habit => 
      habit.startsWith(this.lifeStyleHabitQuestionnaire?.diseaseGroups[3]?.diseaseType ?? '')
    );
  }

  // 勾選/取消生活習慣checkbox
  onLifeHabitChange(
    diseaseType: string,
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    // 標記表單已修改
    this.markFormDirty();
    const value = diseaseType + '_' + diseaseOption.name;
    
    if (checked) {
      // 【新增】如果勾選的是「以下皆無」，清空其他選項
      if (diseaseOption.name === '以下皆無') {
        // 清空該組的其他選項
        const currentGroup = this.lifeStyleHabitQuestionnaire?.diseaseGroups[3];
        if (currentGroup) {
          currentGroup.diseaseOptions.forEach(option => {
            if (option.name !== '以下皆無') {
              const idx = this.lifeStyleHabits.indexOf(diseaseType + '_' + option.name);
              if (idx > -1) {
                this.lifeStyleHabits.splice(idx, 1);
              }
              this.lifeStyleHabitCheck.questionAnswers =
                this.lifeStyleHabitCheck.questionAnswers.filter(
                  ans => ans.choiceId !== option.choiceId
                );
            }
          });
        }
      }
      
      this.lifeStyleHabits.push(value);
      if (
        !this.lifeStyleHabitCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.lifeStyleHabitCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
      
      // 只清除 checkbox 題目的錯誤狀態 (第3題)
      this.lifeStyleErrors[3] = false;
    } else {
      const idx = this.lifeStyleHabits.indexOf(value);
      if (idx > -1) this.lifeStyleHabits.splice(idx, 1);
      this.lifeStyleHabitCheck.questionAnswers =
        this.lifeStyleHabitCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });
    }
  }

  // 選擇生活習慣(radio button)
  onLifeHabitRadioChange(
    diseaseType: string,
    diseaseOption: DiseaseOption,
    groupIndex: number
  ) {
    // 標記表單已修改
    this.markFormDirty();
    // 清除該組的其他選項
    const currentGroup = this.lifeStyleHabitQuestionnaire?.diseaseGroups[groupIndex];
    if (currentGroup) {
      currentGroup.diseaseOptions.forEach(option => {
        const idx = this.lifeStyleHabits.indexOf(diseaseType + '_' + option.name);
        if (idx > -1) {
          this.lifeStyleHabits.splice(idx, 1);
        }
        this.lifeStyleHabitCheck.questionAnswers =
          this.lifeStyleHabitCheck.questionAnswers.filter(
            ans => ans.choiceId !== option.choiceId
          );
      });
    }

    // 添加新選擇的選項
    this.lifeStyleHabits.push(diseaseType + '_' + diseaseOption.name);
    this.lifeStyleHabitCheck.questionAnswers.push({
      choiceId: diseaseOption.choiceId,
      answer: diseaseOption.answer,
    });

    // 重置數字輸入欄位
    this.resetLifeStyleNumberInputs(groupIndex);

    // 只清除這一題的錯誤狀態
    this.lifeStyleErrors[groupIndex] = false;
  }

  // 生活習慣驗證
  isLifeHabitValid(): boolean {
    // 生活習慣前三個問題為radio button，每題都必須選擇
    const radioValid = (
      this.selectedLifeStyleHabits[0] !== undefined &&
      this.selectedLifeStyleHabits[1] !== undefined &&
      this.selectedLifeStyleHabits[2] !== undefined
    );
    
    // 檢查第4個問題（checkbox）是否至少選擇一項
    const checkboxValid = this.lifeStyleHabits.some(habit => 
      habit.startsWith(this.lifeStyleHabitQuestionnaire?.diseaseGroups[3]?.diseaseType ?? '')
    );
    
    return radioValid && checkboxValid;
  }

  setLifeStyleErrors(): void {
    // 檢查前3題 radio button
    for (let i = 0; i < 3; i++) {
      this.lifeStyleErrors[i] = !this.isLifeStyleQuestionValid(i);
    }
    
    // 檢查第4題 checkbox
    this.lifeStyleErrors[3] = !this.isLifeStyleQuestionValid(3);
  }

  // 修改運動習慣驗證方法
  isExerciseQuestionValid(index: number): boolean {
    if (this.hasExerciseHabit === '') {
      if (index === 0) return false; // 第一題必須選擇
      return true;
    }

    if (this.hasExerciseHabit === API_PARAMS.NO) {
      // 沒有運動習慣時，檢查題目 1, 2, 4
      if (index === 1 || index === 2) {
        return this.selectedExerciseHabits[index] !== undefined;
      } else if (index === 3) {
        // Checkbox question for no exercise habit
        return this.exerciseHabits.some(habit =>
          habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[3]?.diseaseType ?? '')
        );
      } else if (index === 4) {
        // 運動禁忌 (checkbox) - 檢查是否有選擇任何選項
        const diseaseType = this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType ?? '';
        const prohibitionHabits = this.exerciseHabits.filter(habit => habit.startsWith(diseaseType));

        return prohibitionHabits.length > 0;
      }
    } else {
      // 有運動習慣時，檢查題目 4, 6, 7, 8
      if (index === 6 || index === 7 || index === 8) {
        return this.selectedExerciseHabits[index] !== undefined;
      } else if (index === 4) {
        // 運動禁忌 (checkbox) - 檢查是否有選擇任何選項
        const diseaseType = this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType ?? '';
        const prohibitionHabits = this.exerciseHabits.filter(habit => habit.startsWith(diseaseType));

        return prohibitionHabits.length > 0;
      } else if (index === 5) {
        // Checkbox question for exercise habit
        return this.exerciseHabits.some(habit =>
          habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[5]?.diseaseType ?? '')
        );
      }
    }
    return true;
  }

  /**
   * 檢查飲食習慣是否選擇了「以下皆無」
   */
  isDietHabitNone(): boolean {
    const noneOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseOptions
      .find(opt => opt.name === '以下皆無');
    
    if (!noneOption) return false;
    
    return this.lifeStyleHabits.includes(
      HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType + '_' + noneOption.name
    );
  }

  // 是否有運動習慣(運動習慣第一題)變更時觸發
  onHashExerciseHabitChange() {
    // 標記表單已修改
    this.markFormDirty();
    if (this.hasExerciseHabit === API_PARAMS.NO) {
      // 清空6~9題的選項
      delete this.selectedExerciseHabits[5];
      this.exerciseHabitQuestionnaire?.diseaseGroups[5].diseaseOptions.forEach(
        (option: DiseaseOption) => {
          this.onExerciseHabitChange(
            this.exerciseHabitQuestionnaire?.diseaseGroups[5].diseaseType!,
            false,
            option
          );
        }
      );

      delete this.selectedExerciseHabits[6];
      this.exerciseHabits = this.exerciseHabits.filter(
        habit =>
          !habit.startsWith(
            this.exerciseHabitQuestionnaire?.diseaseGroups[6].diseaseType ?? ''
          )
      );

      delete this.selectedExerciseHabits[7];
      this.exerciseHabits = this.exerciseHabits.filter(
        habit =>
          !habit.startsWith(
            this.exerciseHabitQuestionnaire?.diseaseGroups[7].diseaseType ?? ''
          )
      );

      delete this.selectedExerciseHabits[8];
      this.exerciseHabits = this.exerciseHabits.filter(
        habit =>
          !habit.startsWith(
            this.exerciseHabitQuestionnaire?.diseaseGroups[8].diseaseType ?? ''
          )
      );
    } else {
      // 清空2~4題的選項
      delete this.selectedExerciseHabits[1];
      this.exerciseHabits = this.exerciseHabits.filter(
        habit =>
          !habit.startsWith(
            this.exerciseHabitQuestionnaire?.diseaseGroups[1].diseaseType ?? ''
          )
      );

      delete this.selectedExerciseHabits[2];
      this.exerciseHabits = this.exerciseHabits.filter(
        habit =>
          !habit.startsWith(
            this.exerciseHabitQuestionnaire?.diseaseGroups[2].diseaseType ?? ''
          )
      );

      delete this.selectedExerciseHabits[3];
      this.exerciseHabitQuestionnaire?.diseaseGroups[3].diseaseOptions.forEach(
        (option: DiseaseOption) => {
          this.onExerciseHabitChange(
            this.exerciseHabitQuestionnaire?.diseaseGroups[3].diseaseType!,
            false,
            option
          );
        }
      );
    }
    this.exerciseHabitErrors[0] = false;
  }

  // 運動習慣問題是否enable
  isExerciseQuestionEnabled(index: number): boolean {
    if (this.hasExerciseHabit === '') return true; // 如果還沒選擇是否有運動習慣，則顯示所有問題
    if (this.hasExerciseHabit === API_PARAMS.NO) {
      return index <= 4; // 如果沒有運動習慣，則顯示問題2~5
    } else {
      return index >= 4; // 如果有運動習慣，則顯示問題5~9
    }
  }

  // 檢查運動習慣 radio button 是否都有選擇
  isExerciseRadioValid(): boolean {
    if (this.hasExerciseHabit === '') return false;
    if (this.hasExerciseHabit === API_PARAMS.NO) {
      return (
        this.selectedExerciseHabits[1] !== undefined &&
        this.selectedExerciseHabits[2] !== undefined &&
        this.selectedExerciseHabits[4] !== undefined
      );
    } else {
      return (
        this.selectedExerciseHabits[4] !== undefined &&
        this.selectedExerciseHabits[6] !== undefined &&
        this.selectedExerciseHabits[7] !== undefined &&
        this.selectedExerciseHabits[8] !== undefined
      );
    }
  }

  // 檢查運動習慣 checkbox 是否有選擇
  isExerciseCheckboxValid(): boolean {
    // 運動禁忌（第4個問題）是必填的，無論是否有運動習慣都需要檢查
    const hasProhibitionSelected = this.exerciseHabits.some(habit =>
      habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType ?? '')
    );

    if (!hasProhibitionSelected) {
      return false;
    }

    if (this.hasExerciseHabit === API_PARAMS.NO) {
      // 如果沒有運動習慣，需要檢查第3個問題的checkbox
      return this.exerciseHabits.some(habit =>
        habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[3]?.diseaseType ?? '')
      );
    } else if (this.hasExerciseHabit === API_PARAMS.YES) {
      // 如果有運動習慣，需要檢查第5個問題的checkbox
      return this.exerciseHabits.some(habit =>
        habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[5]?.diseaseType ?? '')
      );
    }
    return true;
  }

  // 勾選/取消生活習慣checkbox
  onExerciseHabitChange(
    diseaseType: string,
    checked: boolean,
    diseaseOption: DiseaseOption
  ) {
    // 標記表單已修改
    this.markFormDirty();
    const value = diseaseType + '_' + diseaseOption.name;

    if (checked) {
      if (diseaseType === '運動禁忌(可複選)' && diseaseOption.name === '無') {
        // 清空該組的其他選項
        const currentGroup = this.exerciseHabitQuestionnaire?.diseaseGroups[4];
        if (currentGroup) {
          currentGroup.diseaseOptions.forEach(option => {
            if (option.name !== '無') {
              // 使用 filter 移除所有匹配項目（處理重複項目）
              this.exerciseHabits = this.exerciseHabits.filter(habit => habit !== diseaseType + '_' + option.name);

              this.exerciseHabitCheck.questionAnswers =
                this.exerciseHabitCheck.questionAnswers.filter(
                  ans => ans.choiceId !== option.choiceId
                );
            }
          });

          // 強制觸發變更檢測，確保 UI 更新
          this.cdr.detectChanges();
        }
      }

      if (diseaseType === '運動禁忌(可複選)' && diseaseOption.name !== '無') {
        const noneOption = this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseOptions
          .find(opt => opt.name === '無');
        if (noneOption) {
          const noneValue = diseaseType + '_無';
          const idx = this.exerciseHabits.indexOf(noneValue);
          if (idx > -1) {
            this.exerciseHabits.splice(idx, 1);
          }
          this.exerciseHabitCheck.questionAnswers =
            this.exerciseHabitCheck.questionAnswers.filter(
              ans => ans.choiceId !== noneOption.choiceId
            );
        }
      }

      this.exerciseHabits.push(value);

      if (
        !this.exerciseHabitCheck.questionAnswers.some(
          ans => ans.choiceId === diseaseOption.choiceId
        )
      ) {
        this.exerciseHabitCheck.questionAnswers.push({
          choiceId: diseaseOption.choiceId,
          answer: diseaseOption.answer,
        });
      }
      
      // 根據具體的 diseaseType 來判斷是哪一題，只清除對應題目的錯誤
      if (this.hasExerciseHabit === API_PARAMS.NO) {
        // 沒有運動習慣時，第3題是 checkbox
        if (diseaseType === this.exerciseHabitQuestionnaire?.diseaseGroups[3]?.diseaseType) {
          this.exerciseHabitErrors[3] = false;
        }
        // 運動禁忌（第4題）是 checkbox
        if (diseaseType === this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType) {
          this.exerciseHabitErrors[4] = false;
        }
      } else {
        // 有運動習慣時，第5題是 checkbox
        if (diseaseType === this.exerciseHabitQuestionnaire?.diseaseGroups[5]?.diseaseType) {
          this.exerciseHabitErrors[5] = false;
        }
        // 運動禁忌（第4題）是 checkbox
        if (diseaseType === this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType) {
          this.exerciseHabitErrors[4] = false;
        }
      }
    } else {
      // 移除所有匹配的項目（處理重複項目問題）
      this.exerciseHabits = this.exerciseHabits.filter(habit => habit !== value);

      this.exerciseHabitCheck.questionAnswers =
        this.exerciseHabitCheck.questionAnswers.filter(ans => {
          return ans.choiceId !== diseaseOption.choiceId;
        });

      // 取消勾選後重新檢查運動禁忌驗證狀態
      if (diseaseType === '運動禁忌(可複選)') {
        const prohibitionHabits = this.exerciseHabits.filter(habit => habit.startsWith('運動禁忌(可複選)'));
        this.exerciseHabitErrors[4] = prohibitionHabits.length === 0;

        // 強制觸發變更檢測，確保 disabled 狀態更新
        this.cdr.detectChanges();
      }
    }
  }



  /**
   * 檢查運動禁忌是否選擇了「無」
   */
  isExerciseProhibitionNone(): boolean {
    const noneOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseOptions
      .find(opt => opt.name === '無');

    if (!noneOption) return false;

    return this.exerciseHabits.includes(
      HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseType + '_' + noneOption.name
    );
  }

  /**
   * 檢查運動禁忌選項是否應該被 disabled
   */
  isExerciseProhibitionDisabled(optionName: string): boolean {
    if (optionName === '無') {
      // 「無」選項：永遠不 disabled，讓用戶可以隨時選擇來清除其他選項
      return false;
    } else {
      // 其他選項：只有當「無」被選擇時才 disabled
      return this.isExerciseProhibitionNone();
    }
  }

  // 選擇生活習慣(radio button)
  onExerciseHabitRadioChange(
    diseaseType: string,
    diseaseOption: DiseaseOption,
    groupIndex: number
  ) {
    // 標記表單已修改
    this.markFormDirty();
    // 清除該組的其他選項
    const currentGroup =
      this.exerciseHabitQuestionnaire?.diseaseGroups[groupIndex];
    if (currentGroup) {
      currentGroup.diseaseOptions.forEach(option => {
        const idx = this.exerciseHabits.indexOf(
          diseaseType + '_' + option.name
        );
        if (idx > -1) {
          this.exerciseHabits.splice(idx, 1);
        }
        this.exerciseHabitCheck.questionAnswers =
          this.exerciseHabitCheck.questionAnswers.filter(
            ans => ans.choiceId !== option.choiceId
          );
      });
    }

    // 添加新選擇的選項
    this.exerciseHabits.push(diseaseType + '_' + diseaseOption.name);
    this.exerciseHabitCheck.questionAnswers.push({
      choiceId: diseaseOption.choiceId,
      answer: diseaseOption.answer,
    });

    this.exerciseHabitErrors[groupIndex] = false;
  }

  // 生活習慣驗證
  isExerciseHabitValid(): boolean {
    if (this.hasExerciseHabit === '') return false;

    if (this.hasExerciseHabit === API_PARAMS.NO) {
      // 無運動習慣：檢查題目 1, 2, 4
      const hasQuestion1 = this.selectedExerciseHabits[1] !== undefined;
      const hasQuestion2 = this.selectedExerciseHabits[2] !== undefined;
      // 運動禁忌 (checkbox) - 檢查是否有選擇任何選項
      const hasProhibition = this.exerciseHabits.some(habit =>
        habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType ?? '')
      );

      console.log('運動習慣整體驗證 (無運動習慣):', {
        hasQuestion1,
        hasQuestion2,
        hasProhibition,
        isValid: hasQuestion1 && hasQuestion2 && hasProhibition
      });

      return hasQuestion1 && hasQuestion2 && hasProhibition;
    } else {
      // 有運動習慣：檢查題目 4, 6, 7, 8
      // 運動禁忌 (checkbox) - 檢查是否有選擇任何選項
      const hasProhibition = this.exerciseHabits.some(habit =>
        habit.startsWith(this.exerciseHabitQuestionnaire?.diseaseGroups[4]?.diseaseType ?? '')
      );
      const hasQuestion6 = this.selectedExerciseHabits[6] !== undefined;
      const hasQuestion7 = this.selectedExerciseHabits[7] !== undefined;
      const hasQuestion8 = this.selectedExerciseHabits[8] !== undefined;

      console.log('運動習慣整體驗證 (有運動習慣):', {
        hasProhibition,
        hasQuestion6,
        hasQuestion7,
        hasQuestion8,
        isValid: hasProhibition && hasQuestion6 && hasQuestion7 && hasQuestion8
      });

      return hasProhibition && hasQuestion6 && hasQuestion7 && hasQuestion8;
    }
  }

  setExerciseHabitErrors(): void {
    // 第一題：是否有運動習慣
    this.exerciseHabitErrors[0] = this.hasExerciseHabit === '';
    
    if (this.hasExerciseHabit === API_PARAMS.NO) {
      // 檢查題目 1, 2, 3, 4
      for (const i of [1, 2, 3, 4]) {
        this.exerciseHabitErrors[i] = !this.isExerciseQuestionValid(i);
      }
    } else if (this.hasExerciseHabit === API_PARAMS.YES) {
      // 檢查題目 4, 5, 6, 7, 8
      for (const i of [4, 5, 6, 7, 8]) {
        this.exerciseHabitErrors[i] = !this.isExerciseQuestionValid(i);
      }
    }
  }

  // 勾選/取消居住國家
  onResidenceCountryChange(checked: boolean, value: string) {
    // 標記表單已修改
    this.markFormDirty();
    if (checked) {
      this.residenceCountries.push(value);
      // 清除錯誤狀態
      this.showResidenceCountryError = false;
    } else {
      const idx = this.residenceCountries.indexOf(value);
      if (idx > -1) {
        this.residenceCountries.splice(idx, 1);
      }
      // 如果取消勾選"其他"，清空其他居住國家輸入
      if (value === '6') {
        this.otherResidenceCountry = '';
      }
    }
  }

  // 選擇婚姻狀況時清除錯誤
  onMaritalStatusChange(): void {
    this.markFormDirty();
    this.showMaritalStatusError = false;
  }

  // 選擇教育程度時清除錯誤
  onEducationLevelChange(): void {
    this.markFormDirty();
    this.showEducationLevelError = false;
  }

  // 選擇職位時清除錯誤
  onPositionChange(): void {
    this.markFormDirty();
    this.showPositionError = false;
  }

  // 輪班制選項變更時的處理
  onShiftTypeChange(): void {
    this.markFormDirty();
    const currentType = this.occupation.value.type;

    // 如果選擇「否」或不是「其他」輪班制，清空輪班方式說明
    if (currentType === 'needs_shift_no' || currentType !== 'shift_type_4') {
      this.occupation.patchValue({
        shiftDescription: ''
      });
    }
  }


  onOtherResidenceInputChange(event: Event): void {
    // 標記表單已修改
    this.markFormDirty();
    const inputValue = (event.target as HTMLInputElement).value;
    this.otherResidenceCountry = inputValue;

    if (inputValue?.trim()) {
      // 清除錯誤狀態
      this.showResidenceCountryError = false;
    }
  }

  onOtherPositionInputChange(event: Event): void {
    this.markFormDirty();
    const inputValue = (event.target as HTMLInputElement).value;
    this.otherPosition = inputValue;

    if (inputValue?.trim()) {
      this.showPositionError = false; // 清除錯誤狀態
    }
  }

  /**
   * 載入其他步驟的已儲存資料
   */
  private loadOtherStepsData(questions: Questions[]): void {
    console.log('載入其他步驟資料，總共有', questions.length, '個問題');

    // 載入個人病史詳細資料
    console.log('載入個人病史詳細資料...');
    this.loadPersonalHistoryDetails(questions);

    // 載入家族病史詳細資料
    console.log('載入家族病史詳細資料...');
    this.loadFamilyHistoryDetails(questions);

    // 載入自覺症狀詳細資料
    console.log('載入自覺症狀詳細資料...');
    this.loadSubjectiveSymptomDetails(questions);

    // 載入長期藥物資料
    console.log('載入長期藥物資料...');
    this.loadLongTermMedicationData(questions);

    // 載入生活習慣資料
    console.log('載入生活習慣資料...');
    this.loadLifestyleData(questions);

    // 載入手術史資料
    console.log('載入手術史資料...');
    this.loadSurgeryHistoryData(questions);

    // 載入作業經歷資料
    console.log('載入作業經歷資料...');
    this.loadOccupationData(questions);

    // 載入其他資訊資料
    console.log('載入其他資訊資料...');
    this.loadOtherInfoData(questions);

    console.log('所有資料載入完成');

    // 移除狀態判斷
    // this.determineQuestionnaireStatusAfterLoad();
  }

  /**
   * 載入長期藥物資料
   */
  private loadLongTermMedicationData(questions: any[]): void {
    // 長期服用藥物 (questionId: 481)
    const longTermMedicationQuestion = questions.find(q => q.questionId === '481');
    if (longTermMedicationQuestion?.questionAnswers?.length > 0) {
      longTermMedicationQuestion.questionAnswers.forEach((answer: any) => {
        // 檢查是否為「以下皆無」
        if (answer.choiceId === '1575' && answer.answer === '1') {
          this.longTermMedicationNoneOfFollowing = true;
          // 當選擇「以下皆無」時，也要加入到 longTermMedications 陣列和 questionAnswers 中
          this.longTermMedications = [NONE_OF_FOLLOWING];
          this.longTermMedicationCheck.questionAnswers = [{
            choiceId: answer.choiceId,
            answer: answer.answer
          }];
        } else {
          // 尋找其他藥物選項
          const medicationOption = HEALTH_QUESTIONNAIRE_FIELDS.LONG_TERM_MEDICATIONS.diseaseGroups
            .flatMap(group => group.diseaseOptions)
            .find(opt => opt.choiceId === answer.choiceId);

          if (medicationOption) {
            // 加入到長期藥物檢查中
            this.longTermMedicationCheck.questionAnswers.push({
              choiceId: medicationOption.choiceId,
              answer: medicationOption.answer
            });
            // 加入到長期藥物陣列
            this.longTermMedications.push(medicationOption.name);
          }
        }
      });
    }

    // 其他藥物說明 (questionId: 482)
    const otherMedicationQuestion = questions.find(q => q.questionId === '482');
    if (otherMedicationQuestion?.questionAnswers?.length > 0) {
      this.otherLongTermMedication = otherMedicationQuestion.questionAnswers[0].answer;
      this.hasOtherLongTermMedication = true;
    }
  }

  /**
   * 載入生活習慣資料
   */
  private loadLifestyleData(questions: any[]): void {
    // 檳榔 (questionId: 485)
    const betelNutQuestion = questions.find(q => q.questionId === '485');
    if (betelNutQuestion?.questionAnswers?.length > 0) {
      const answer = betelNutQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const betelOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (betelOption) {
        this.selectedLifeStyleHabits[0] = betelOption.name;
      }
    }

    // 檳榔詳細資料 (questionId: 486, 487, 488, 489)
    const betelNutDetailQuestions = questions.filter(q =>
      ['486', '487', '488', '489'].includes(q.questionId)
    );
    betelNutDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '486': // 每天顆數
            this.betelNutPerDay.setValue(parseInt(answer) || 0);
            break;
          case '487': // 嚼食年數
            this.betelNutYears.setValue(parseInt(answer) || 0);
            break;
          case '488': // 戒食年數
            this.betelNutQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '489': // 月數（可能是戒食月數或嚼食月數）
            // 根據當前選擇的檳榔選項決定載入到哪個欄位
            if (this.selectedLifeStyleHabits[0] === '已經戒食') {
              this.betelNutQuitMonths.setValue(parseInt(answer) || 0);
            } else if (this.selectedLifeStyleHabits[0] === '幾乎每天嚼') {
              this.betelNutMonths.setValue(parseInt(answer) || 0);
            }
            break;
        }
      }
    });

    // 吸菸 (questionId: 490)
    const smokingQuestion = questions.find(q => q.questionId === '490');
    if (smokingQuestion?.questionAnswers?.length > 0) {
      const answer = smokingQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const smokingOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[1].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (smokingOption) {
        this.selectedLifeStyleHabits[1] = smokingOption.name;
      }
    }

    // 吸菸詳細資料 (questionId: 491, 492, 493, 494)
    const smokingDetailQuestions = questions.filter(q =>
      ['491', '492', '493', '494'].includes(q.questionId)
    );
    smokingDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '491': // 每天支數
            this.cigarettesPerDay.setValue(parseInt(answer) || 0);
            break;
          case '492': // 吸菸年數
            this.smokingYears.setValue(parseInt(answer) || 0);
            break;
          case '493': // 戒菸年數
            this.smokingQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '494': // 戒菸月數
            this.smokingQuitMonths.setValue(parseInt(answer) || 0);
            break;
        }
      }
    });

    // 飲酒 (questionId: 495)
    const alcoholQuestion = questions.find(q => q.questionId === '495');
    if (alcoholQuestion?.questionAnswers?.length > 0) {
      const answer = alcoholQuestion.questionAnswers[0];
      // 根據 answer 找到對應的選項名稱
      const alcoholOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[2].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (alcoholOption) {
        this.selectedLifeStyleHabits[2] = alcoholOption.name;
      }
    }

    // 飲酒詳細資料 (questionId: 496, 497, 498, 499, 500)
    const alcoholDetailQuestions = questions.filter(q =>
      ['496', '497', '498', '499', '500'].includes(q.questionId)
    );
    alcoholDetailQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        const answer = question.questionAnswers[0].answer;
        switch (question.questionId) {
          case '496': // 每週次數
            this.alcoholTimesPerWeek.setValue(parseInt(answer) || 0);
            break;
          case '497': // 酒類
            this.alcoholType.setValue(answer);
            break;
          case '498': // 每天cc數
            this.alcoholAmountCc.setValue(parseInt(answer) || 0);
            break;
          case '499': // 戒酒年數
            this.alcoholQuitYears.setValue(parseInt(answer) || 0);
            break;
          case '500': // 戒酒月數
            this.alcoholQuitMonths.setValue(parseInt(answer) || 0);
            break;
        }
      }
    });

    // 飲食習慣 (questionId: 501)
    const dietHabitQuestion = questions.find(q => q.questionId === '501');
    if (dietHabitQuestion?.questionAnswers?.length > 0) {
      // 清空現有的飲食習慣
      this.lifeStyleHabits = this.lifeStyleHabits.filter(habit =>
        !habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType)
      );

      // 載入飲食習慣選項
      dietHabitQuestion.questionAnswers.forEach((answer: any) => {
        const dietOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseOptions
          .find(opt => opt.answer === answer.answer);
        if (dietOption) {
          const habitValue = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType + '_' + dietOption.name;
          this.lifeStyleHabits.push(habitValue);
        }
      });
    }

    // 運動習慣 (questionId: 502) - 是否有運動習慣
    const exerciseHabitQuestion = questions.find(q => q.questionId === '502');
    if (exerciseHabitQuestion?.questionAnswers?.length > 0) {
      const answer = exerciseHabitQuestion.questionAnswers[0];
      // 根據 answer 判斷是否有運動習慣
      const exerciseOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.answer === answer.answer);
      if (exerciseOption) {
        this.hasExerciseHabit = exerciseOption.name.includes('無') ? API_PARAMS.NO : API_PARAMS.YES;
      }
    }

    // 載入運動習慣的其他問題答案
    this.loadExerciseHabitDetails(questions);
  }

  /**
   * 載入運動習慣詳細資料
   */
  private loadExerciseHabitDetails(questions: any[]): void {
    // 載入運動習慣的各種問題答案
    const exerciseQuestions = questions.filter(q =>
      ['503', '504', '505', '506', '507', '508', '509', '510'].includes(q.questionId)
    );

    exerciseQuestions.forEach(question => {
      if (question.questionAnswers?.length > 0) {
        switch (question.questionId) {
          case '503': // 能夠舒適走多久
            const walkOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[1].diseaseOptions
              .find(opt => opt.answer === question.questionAnswers[0].answer);
            if (walkOption) {
              this.selectedExerciseHabits[1] = walkOption.name;
            }
            break;
          case '504': // 爬樓梯會喘嗎
            const stairOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[2].diseaseOptions
              .find(opt => opt.answer === question.questionAnswers[0].answer);
            if (stairOption) {
              this.selectedExerciseHabits[2] = stairOption.name;
            }
            break;
          case '505': // 運動喜好 (checkbox)
            question.questionAnswers.forEach((answer: any) => {
              const prefOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[3].diseaseOptions
                .find(opt => opt.choiceId === answer.choiceId);
              if (prefOption) {
                const habitValue = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[3].diseaseType + '_' + prefOption.name;
                this.exerciseHabits.push(habitValue);
              }
            });
            break;
          case '506': // 運動禁忌 (checkbox)
            question.questionAnswers.forEach((answer: any) => {
              const prohibitionOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseOptions
                .find(opt => opt.choiceId === answer.choiceId);
              if (prohibitionOption) {
                const habitValue = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseType + '_' + prohibitionOption.name;
                this.exerciseHabits.push(habitValue);
              }
            });
            break;
          case '507': // 通常進行哪種運動 (checkbox)
            question.questionAnswers.forEach((answer: any) => {
              const exOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[5].diseaseOptions
                .find(opt => opt.choiceId === answer.choiceId);
              if (exOption) {
                const habitValue = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[5].diseaseType + '_' + exOption.name;
                this.exerciseHabits.push(habitValue);
              }
            });
            break;
          case '508': { // 每周運動時間
            const weeklyTimeOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[6].diseaseOptions
              .find(opt => opt.answer === question.questionAnswers[0].answer);
            if (weeklyTimeOption) {
              this.selectedExerciseHabits[6] = weeklyTimeOption.name;
            }
            break;
          }
          case '509': { // 每次運動時間
            const durationOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[7].diseaseOptions
              .find(opt => opt.answer === question.questionAnswers[0].answer);
            if (durationOption) {
              this.selectedExerciseHabits[7] = durationOption.name;
            }
            break;
          }
          case '510': { // 運動強度
            const intensityOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[8].diseaseOptions
              .find(opt => opt.answer === question.questionAnswers[0].answer);
            if (intensityOption) {
              this.selectedExerciseHabits[8] = intensityOption.name;
            }
            break;
          }
        }
      }
    });
  }

  /**
   * 載入手術史資料
   */
  private loadSurgeryHistoryData(questions: any[]): void {
    // 是否有手術史或重大外傷 (questionId: 479)
    const surgeryQuestion = questions.find(q => q.questionId === '479');
    if (surgeryQuestion?.questionAnswers?.length > 0) {
      const answer = surgeryQuestion.questionAnswers[0];
      // answer.answer: '1' = 無, '2' = 有
      this.hasSurgeryHistoryOrMajorTrauma = answer.answer === '2' ? API_PARAMS.YES : API_PARAMS.NO;
    }

    // 手術史詳細說明 (questionId: 480)
    const surgeryDetailQuestion = questions.find(q => q.questionId === '480');
    if (surgeryDetailQuestion?.questionAnswers?.length > 0) {
      this.surgeryHistoryOrMajorTrauma = surgeryDetailQuestion.questionAnswers[0].answer;
    }
  }



  /**
   * 載入個人病史詳細資料
   */
  private loadPersonalHistoryDetails(questions: any[]): void {
    const personalHistoryQuestion = questions.find(q => q.questionId === '474');
    if (!personalHistoryQuestion?.questionAnswers?.length) {
      console.log('沒有個人病史資料可載入');
      return;
    }

    console.log('開始載入個人病史，原始資料:', personalHistoryQuestion.questionAnswers);

    // 簡化的載入資訊
    console.log(`載入個人病史，共 ${personalHistoryQuestion.questionAnswers.length} 個答案`);

    // 檢查 personalQuestionnaire 是否已載入
    console.log('personalQuestionnaire 狀態:', {
      isLoaded: !!this.personalQuestionnaire,
      diseaseGroupsCount: this.personalQuestionnaire?.diseaseGroups?.length || 0
    });

    if (!this.personalQuestionnaire?.diseaseGroups?.length) {
      console.log('personalQuestionnaire 尚未載入，但仍保存原始資料');
      // 不要返回，保留原始的 questionAnswers 資料
      console.log('載入個人病史詳細資料完成 (僅保存原始資料):', {
        personalDiseases: this.personalDiseases,
        personalDiseaseDetails: this.personalDiseaseDetails,
        personalNoneOfFollowing: this.personalNoneOfFollowing,
        otherPersonalDisease: this.otherPersonalDisease,
        questionAnswers: this.personalDiseaseCheck.questionAnswers
      });
      return;
    }

    // 過濾掉應該在其他 questionId 的 choiceId
    // 1568(骨折詳細)->475, 1569(癌症詳細)->476, 1570(其他慢性病詳細)->477, 1571(其他病史)->478
    const filteredPersonalAnswers = personalHistoryQuestion.questionAnswers.filter(
      (ans: any) => !['1568', '1569', '1570', '1571'].includes(ans.choiceId)
    );
    
    // 重建 personalDiseaseCheck
    this.personalDiseaseCheck.questionAnswers = filteredPersonalAnswers;

    // 重建 personalDiseases 和 personalDiseaseDetails
    this.personalDiseases = [];
    this.personalDiseaseDetails = {};
    this.personalNoneOfFollowing = false;
    this.otherPersonalDisease = '';
    
    // 載入其他病史說明 (從 questionId: 478 載入)
    const otherPersonalDiseaseQuestion = questions.find(q => q.questionId === '478');
    if (otherPersonalDiseaseQuestion?.questionAnswers?.length > 0) {
      this.otherPersonalDisease = otherPersonalDiseaseQuestion.questionAnswers[0].answer;
      console.log('載入其他病史說明 (從 questionId 478):', this.otherPersonalDisease);
    }

    // 先處理詳細說明欄位，建立 personalDiseaseDetails
    filteredPersonalAnswers.forEach((answer: any) => {
      // 檢查是否為"以下皆無"
      if (answer.choiceId === '1540') { // 以下皆無的 choiceId
        this.personalNoneOfFollowing = true;
        const value = this.personalHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.personalDiseases = [value];
        console.log('載入「以下皆無」');
        return;
      }

      // 特別處理癌症詳細說明 (從 questionId: 476 載入)
      if (answer.choiceId === '1569') {
        const cancerDetailQuestion = questions.find(q => q.questionId === '476');
        if (cancerDetailQuestion?.questionAnswers?.length > 0) {
          this.personalDiseaseDetails['1565'] = cancerDetailQuestion.questionAnswers[0].answer;
          console.log('載入癌症詳細說明 (從 questionId 476):', {
            choiceId: '1565',
            detailText: cancerDetailQuestion.questionAnswers[0].answer
          });
        }
        return;
      }
      // 特別處理骨折詳細說明 (從 questionId: 475 載入)
      if (answer.choiceId === '1568') {
        const fractureDetailQuestion = questions.find(q => q.questionId === '475');
        if (fractureDetailQuestion?.questionAnswers?.length > 0) {
          this.personalDiseaseDetails['1564'] = fractureDetailQuestion.questionAnswers[0].answer;
          console.log('載入骨折詳細說明 (從 questionId 475):', {
            choiceId: '1564',
            detailText: fractureDetailQuestion.questionAnswers[0].answer
          });
        }
        return;
      }
      
      // 特別處理其他慢性病詳細說明 (從 questionId: 477 載入)
      if (answer.choiceId === '1570') {
        const chronicDiseaseDetailQuestion = questions.find(q => q.questionId === '477');
        if (chronicDiseaseDetailQuestion?.questionAnswers?.length > 0) {
          this.personalDiseaseDetails['1566'] = chronicDiseaseDetailQuestion.questionAnswers[0].answer;
          console.log('載入其他慢性病詳細說明 (從 questionId 477):', {
            choiceId: '1566',
            detailText: chronicDiseaseDetailQuestion.questionAnswers[0].answer
          });
        }
        return;
      }

      // 尋找對應的疾病選項
      for (const diseaseGroup of this.personalQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.detailPlaceholderId === answer.choiceId) {
            // 詳細說明欄位 - 確保屬性存在並設定值
            if (!this.personalDiseaseDetails.hasOwnProperty(diseaseOption.choiceId)) {
              this.personalDiseaseDetails[diseaseOption.choiceId] = '';
            }
            this.personalDiseaseDetails[diseaseOption.choiceId] = answer.answer;
            console.log('載入詳細說明:', {
              disease: diseaseOption.name,
              choiceId: diseaseOption.choiceId,
              detailPlaceholderId: diseaseOption.detailPlaceholderId,
              detailText: answer.answer,
              personalDiseaseDetails: this.personalDiseaseDetails
            });
            break;
          }
        }
      }
    });

    // 再處理主要疾病選項，建立 personalDiseases
    filteredPersonalAnswers.forEach((answer: any) => {
      for (const diseaseGroup of this.personalQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.choiceId === answer.choiceId) {
            // 一般疾病選項 - 直接重建狀態，避免觸發變更檢測錯誤
            console.log('載入疾病選項:', {
              disease: diseaseOption.name,
              choiceId: diseaseOption.choiceId
            });

            // 直接重建 personalDiseases 陣列
            const value = this.personalHistoryDiseasePipe.transform(diseaseOption.name);
            if (!this.personalDiseases.includes(value)) {
              this.personalDiseases.push(value);
            }
            break;
          }
        }
      }
    });

    console.log('個人病史載入完成');

    // 強制觸發變更檢測以更新 input 欄位
    setTimeout(() => {
      console.log('觸發變更檢測以更新 input 欄位');
      this.cdr.detectChanges();
      console.log('變更檢測完成，personalDiseaseDetails:', this.personalDiseaseDetails);
    }, 0);
  }

  /**
   * 載入家族病史詳細資料
   */
  private loadFamilyHistoryDetails(questions: any[]): void {
    const familyHistoryQuestion = questions.find(q => q.questionId === '483');
    if (!familyHistoryQuestion?.questionAnswers?.length) {
      return;
    }

    console.log('開始載入家族病史，共', familyHistoryQuestion.questionAnswers.length, '個答案');

    // 過濾掉 choiceId: 1611 (其他家族病史的輸入內容,應該在 questionId: 484)
    const filteredFamilyAnswers = familyHistoryQuestion.questionAnswers.filter(
      (ans: any) => ans.choiceId !== '1611'
    );
    
    // 重建 familyDiseaseCheck
    this.familyDiseaseCheck.questionAnswers = filteredFamilyAnswers;

    // 重建 familyDiseases 和 familyDiseaseDetails
    this.familyDiseases = [];
    this.familyDiseaseDetails = {};
    this.familyNoneOfFollowing = false;
    this.otherFamilyDisease = '';
    
    // 載入其他家族病史說明 (從 questionId: 484 載入)
    const otherFamilyDiseaseQuestion = questions.find(q => q.questionId === '484');
    if (otherFamilyDiseaseQuestion?.questionAnswers?.length > 0) {
      this.otherFamilyDisease = otherFamilyDiseaseQuestion.questionAnswers[0].answer;
      console.log('載入其他家族病史說明 (從 questionId 484):', this.otherFamilyDisease);
    }

    // 先處理詳細說明欄位
    filteredFamilyAnswers.forEach((answer: any) => {
      // 檢查是否為"以下皆無"
      if (answer.choiceId === '1592') {
        this.familyNoneOfFollowing = true;
        const value = this.familyHistoryDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.familyDiseases = [value];
        return;
      }

      // 尋找對應的疾病選項的詳細說明
      for (const diseaseGroup of this.familyQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.detailPlaceholderId === answer.choiceId) {
            // 詳細說明欄位
            if (!this.familyDiseaseDetails.hasOwnProperty(diseaseOption.choiceId)) {
              this.familyDiseaseDetails[diseaseOption.choiceId] = '';
            }
            this.familyDiseaseDetails[diseaseOption.choiceId] = answer.answer;
            console.log('載入家族病史詳細說明:', {
              disease: diseaseOption.name,
              choiceId: diseaseOption.choiceId,
              detailText: answer.answer
            });
            break;
          }
        }
      }
    });

    // 再處理主要疾病選項
    filteredFamilyAnswers.forEach((answer: any) => {
      for (const diseaseGroup of this.familyQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.choiceId === answer.choiceId) {
            const value = this.familyHistoryDiseasePipe.transform(diseaseOption.name);
            if (!this.familyDiseases.includes(value)) {
              this.familyDiseases.push(value);
            }
            break;
          }
        }
      }
    });

    console.log('家族病史載入完成');
    
    // 強制觸發變更檢測
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * 載入自覺症狀詳細資料
   */
  private loadSubjectiveSymptomDetails(questions: any[]): void {
    const subjectiveSymptomQuestion = questions.find(q => q.questionId === '472');
    if (!subjectiveSymptomQuestion?.questionAnswers?.length) {
      console.log('沒有自覺症狀資料可載入');
      return;
    }

    console.log('開始載入自覺症狀，共', subjectiveSymptomQuestion.questionAnswers.length, '個答案');

    // 過濾掉不應該在自覺症狀的 choiceId
    // 1539: 自覺症狀其他輸入 -> 應該在 473
    // 1568: 骨折輸入 -> 應該在 475
    // 1569: 癌症輸入 -> 應該在 476
    // 1570: 其他慢性病輸入 -> 應該在 477
    // 1571: 其他病史輸入 -> 應該在 478
    // 1611: 家族病史其他輸入 -> 應該在 484
    const filteredAnswers = subjectiveSymptomQuestion.questionAnswers.filter(
      (ans: any) => !['1539', '1568', '1569', '1570', '1571', '1611'].includes(ans.choiceId)
    );
    
    // 重建 subjectiveSymptomDiseaseCheck
    this.subjectiveSymptomDiseaseCheck.questionAnswers = filteredAnswers;

    // 重建 subjectiveSymptomDiseases
    this.subjectiveSymptomDiseases = [];
    this.subjectiveSymptomNoneOfFollowing = false;
    this.otherSubjectiveSymptomDisease = ''; // 先清空
    
    // 載入其他自覺症狀說明 (從 questionId: 473 載入)
    const otherSubjectiveSymptomQuestion = questions.find(q => q.questionId === '473');
    if (otherSubjectiveSymptomQuestion?.questionAnswers?.length > 0) {
      this.otherSubjectiveSymptomDisease = otherSubjectiveSymptomQuestion.questionAnswers[0].answer;
      console.log('載入其他自覺症狀說明 (從 questionId 473):', this.otherSubjectiveSymptomDisease);
    }

    filteredAnswers.forEach((answer: any) => {
      // 檢查是否為"以下皆無"
      if (answer.choiceId === '1510') {
        this.subjectiveSymptomNoneOfFollowing = true;
        const value = this.subjectiveSymptomDiseasePipe.transform(NONE_OF_FOLLOWING);
        this.subjectiveSymptomDiseases = [value];
        console.log('載入「以下皆無」');
        return;
      }

      // 尋找對應的症狀選項
      for (const diseaseGroup of this.subjectiveSymptomQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.choiceId === answer.choiceId) {
            const value = this.subjectiveSymptomDiseasePipe.transform(diseaseOption.name);
            if (!this.subjectiveSymptomDiseases.includes(value)) {
              this.subjectiveSymptomDiseases.push(value);
            }
            console.log('載入症狀選項:', diseaseOption.name);
            break;
          }
        }
      }
    });

    console.log('自覺症狀載入完成', {
      subjectiveSymptomDiseases: this.subjectiveSymptomDiseases,
      otherSubjectiveSymptomDisease: this.otherSubjectiveSymptomDisease
    });
  }

  /**
   * 載入其他資訊資料
   */
  private loadOtherInfoData(questions: any[]): void {
    // 常住國家 (questionId: 511)
    const residenceCountryQuestion = questions.find(q => q.questionId === '511');
    if (residenceCountryQuestion?.questionAnswers?.length > 0) {
      this.residenceCountries = residenceCountryQuestion.questionAnswers.map((answer: any) => answer.answer);
    }

    // 常住國家其他 (questionId: 512)
    const residenceCountryOtherQuestion = questions.find(q => q.questionId === '512');
    if (residenceCountryOtherQuestion?.questionAnswers?.length > 0) {
      this.otherResidenceCountry = residenceCountryOtherQuestion.questionAnswers[0].answer;
    }

    // 婚姻狀況 (questionId: 513)
    const maritalStatusQuestion = questions.find(q => q.questionId === '513');
    if (maritalStatusQuestion?.questionAnswers?.length > 0) {
      this.maritalStatus = maritalStatusQuestion.questionAnswers[0].answer;
    }

    // 教育程度 (questionId: 514)
    const educationLevelQuestion = questions.find(q => q.questionId === '514');
    if (educationLevelQuestion?.questionAnswers?.length > 0) {
      this.educationLevel = educationLevelQuestion.questionAnswers[0].answer;
    }

    // 職位 (questionId: 515)
    const positionQuestion = questions.find(q => q.questionId === '515');
    if (positionQuestion?.questionAnswers?.length > 0) {
      this.position = positionQuestion.questionAnswers[0].answer;
    }

    // 職位其他 (questionId: 516)
    const positionOtherQuestion = questions.find(q => q.questionId === '516');
    if (positionOtherQuestion?.questionAnswers?.length > 0) {
      this.otherPosition = positionOtherQuestion.questionAnswers[0].answer;
    }

    // 載入作業經歷資料
    this.loadOccupationData(questions);
  }

  /**
   * 載入作業經歷資料
   */
  private loadOccupationData(questions: any[]): void {
    // ========== 目前從事職業 ==========
    
    // 目前從事行業 (questionId: 523)
    const occupationCategoryQuestion = questions.find(q => q.questionId === '523');
    if (occupationCategoryQuestion?.questionAnswers?.length > 0) {
      this.occupation.patchValue({
        category: occupationCategoryQuestion.questionAnswers[0].answer
      });
    }

    // 目前從事行業其他說明 (questionId: 524)
    const occupationOtherQuestion = questions.find(q => q.questionId === '524');
    if (occupationOtherQuestion?.questionAnswers?.length > 0) {
      this.occupation.patchValue({
        otherOccupation: occupationOtherQuestion.questionAnswers[0].answer
      });
    }

    // 受僱日期起 (questionId: 525)
    const employmentDateStartQuestion = questions.find(q => q.questionId === '525');
    if (employmentDateStartQuestion?.questionAnswers?.length > 0) {
      const dateStr = employmentDateStartQuestion.questionAnswers[0].answer;
      const employmentDate = moment(dateStr, 'YYYY/MM');
      if (employmentDate.isValid()) {
        this.occupation.patchValue({
          employmentDate: employmentDate
        });
      }
    }

    // 受僱日期止 (questionId: 526) 
    const employmentDateEndQuestion = questions.find(q => q.questionId === '526');
    if (employmentDateEndQuestion?.questionAnswers?.length > 0) {
      const dateStr = employmentDateEndQuestion.questionAnswers[0].answer;
      const employmentEndDate = moment(dateStr, 'YYYY/MM');
      if (employmentEndDate.isValid()) {
        this.occupation.patchValue({
          employmentEndDate: employmentEndDate
        });
      }
    }

    // 是否需輪班 (questionId: 527) 和 輪班制 (questionId: 528)
    const needsShiftQuestion = questions.find(q => q.questionId === '527');
    const shiftTypeQuestion = questions.find(q => q.questionId === '528');

    console.log('載入輪班資料:', {
      needsShiftQuestion: needsShiftQuestion?.questionAnswers,
      shiftTypeQuestion: shiftTypeQuestion?.questionAnswers
    });

    if (needsShiftQuestion?.questionAnswers?.length > 0) {
      const needsShiftAnswer = needsShiftQuestion.questionAnswers[0].answer;
      console.log('檢測到「是否需輪班」資料:', needsShiftAnswer);

      if (needsShiftAnswer === '1') {
        // 選擇「否」，使用特殊值 'needs_shift_no' 避免與輪班制衝突
        console.log('用戶選擇「否」，載入為不需輪班');
        this.occupation.patchValue({
          type: 'needs_shift_no'
        });
      } else if (needsShiftAnswer === '2') {
        // 選擇「是」，檢查是否有具體的輪班制選項
        console.log('用戶選擇「是」，檢查具體輪班制選項');
        if (shiftTypeQuestion?.questionAnswers?.length > 0) {
          const shiftTypeAnswer = shiftTypeQuestion.questionAnswers[0].answer;
          console.log('載入具體輪班制:', shiftTypeAnswer);
          // 使用特殊值前綴避免衝突
          this.occupation.patchValue({
            type: `shift_type_${shiftTypeAnswer}`
          });
        } else {
          // 沒有具體輪班制，使用「是」
          console.log('沒有具體輪班制，使用「是」');
          this.occupation.patchValue({
            type: 'needs_shift_yes'
          });
        }
      }
    } else if (shiftTypeQuestion?.questionAnswers?.length > 0) {
      // 只有輪班制選項，沒有「是否需輪班」的記錄（舊資料）
      const shiftTypeAnswer = shiftTypeQuestion.questionAnswers[0].answer;
      console.log('只有輪班制選項（舊資料）:', shiftTypeAnswer);
      this.occupation.patchValue({
        type: `shift_type_${shiftTypeAnswer}`
      });
    } else {
      console.log('沒有任何輪班相關資料');
    }

    // 輪班方式說明 (questionId: 529) - 只有在選擇「其他」輪班制時才載入
    const shiftDescriptionQuestion = questions.find(q => q.questionId === '529');
    if (shiftDescriptionQuestion?.questionAnswers?.length > 0) {
      // 檢查當前是否選擇了「其他」輪班制
      const currentType = this.occupation.value.type;
      if (currentType === 'shift_type_4') {
        this.occupation.patchValue({
          shiftDescription: shiftDescriptionQuestion.questionAnswers[0].answer
        });
      }
    }

    // ========== 曾經從事職業 ==========
    
    // 曾經從事行業 (questionId: 519)
    const prevOccupationQuestion = questions.find(q => q.questionId === '519');
    if (prevOccupationQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        category: prevOccupationQuestion.questionAnswers[0].answer
      });
    }

    // 曾經從事行業其他說明 (questionId: 520)
    const prevOccupationOtherQuestion = questions.find(q => q.questionId === '520');
    if (prevOccupationOtherQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        otherOccupation: prevOccupationOtherQuestion.questionAnswers[0].answer
      });
    }

    // 開始年月 (questionId: 521)
    const startDateQuestion = questions.find(q => q.questionId === '521');
    if (startDateQuestion?.questionAnswers?.length > 0) {
      const dateStr = startDateQuestion.questionAnswers[0].answer; // 格式: YYYY/MM
      const [year, month] = dateStr.split('/');
      this.previousOccupation.patchValue({
        startYear: year,
        startMonth: String(parseInt(month))
      });
    }

    // 結束年月 (questionId: 522)
    const endDateQuestion = questions.find(q => q.questionId === '522');
    if (endDateQuestion?.questionAnswers?.length > 0) {
      const dateStr = endDateQuestion.questionAnswers[0].answer; // 格式: YYYY/MM
      const [year, month] = dateStr.split('/');
      this.previousOccupation.patchValue({
        endYear: year,
        endMonth: String(parseInt(month))
      });
    }

    // 過去一個月平均工時 (questionId: 530)
    const avgWorkHoursPastMonthQuestion = questions.find(q => q.questionId === '530');
    if (avgWorkHoursPastMonthQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgWorkHoursPastMonth: parseInt(avgWorkHoursPastMonthQuestion.questionAnswers[0].answer) || 0
      });
    }

    // 過去六個月平均工時 (questionId: 531)
    const avgWorkHoursPastSixMonthsQuestion = questions.find(q => q.questionId === '531');
    if (avgWorkHoursPastSixMonthsQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgWorkHoursPastSixMonths: parseInt(avgWorkHoursPastSixMonthsQuestion.questionAnswers[0].answer) || 0
      });
    }

    // 檢查原因 (questionId: 532)
    const examReasonQuestion = questions.find(q => q.questionId === '532');
    if (examReasonQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        examReason: examReasonQuestion.questionAnswers[0].answer
      });
    }

    // 平均睡眠時間 (questionId: 533)
    const avgSleepTimeQuestion = questions.find(q => q.questionId === '533');
    if (avgSleepTimeQuestion?.questionAnswers?.length > 0) {
      this.previousOccupation.patchValue({
        avgSleepTime: parseInt(avgSleepTimeQuestion.questionAnswers[0].answer) || 0
      });
    }
  }

  // 畫面滾動到頂部
  scrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  // 點擊"上一步"
  previous() {
    this.step--;
    this.showError = false;
    this.scrollToTop();
  }

  // 點擊"下一步"
  next(event: Event) {
    (event.target as HTMLElement).blur();

    // 步驟1：個人病史驗證
    if (this.step === 1) {
      // 檢查是否有勾選任何項目
      const hasPersonalHistory = this.personalNoneOfFollowing || 
                                this.personalDiseases.length > 0 || 
                                Object.entries(this.personalDiseaseDetails).length > 0;
      
      if (!hasPersonalHistory) {
        this.showPersonalHistoryError = true;
        this.openRequiredFieldDialog();
        return;
      }

      // 檢查個人病史詳細症狀是否填寫
      let firstEmptyChoiceId = null;
      
      // 按照 personalQuestionnaire 的順序遍歷 如有未填寫的詳細症狀，則滾動到該欄位
      for (const diseaseGroup of this.personalQuestionnaire?.diseaseGroups || []) {
        for (const diseaseOption of diseaseGroup.diseaseOptions) {
          if (diseaseOption.detailPlaceholder && 
              this.isPersonalDiseaseChecked(diseaseOption.choiceId) &&
              (!this.personalDiseaseDetails[diseaseOption.choiceId] ||
              this.personalDiseaseDetails[diseaseOption.choiceId].trim() === '')) {
            firstEmptyChoiceId = diseaseOption.choiceId;
            break;
          }
        }
        if (firstEmptyChoiceId) break;
      }
      
      if (firstEmptyChoiceId) {
        const element = document.getElementById(`personal-detail-${firstEmptyChoiceId}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.showError = true;
        return;
      }
      
      this.showPersonalHistoryError = false;
      this.showError = false;
    }

    // 步驟2：家族病史驗證
    if (this.step === 2) {
      const hasFamilyHistory = this.familyNoneOfFollowing ||
                              this.familyDiseases.length > 0 ||
                              (this.otherFamilyDisease && this.otherFamilyDisease.trim());

      if (!hasFamilyHistory) {
        this.showFamilyHistoryError = true;
        this.openRequiredFieldDialog();
        return;
      }

      this.familyDiseaseCheck.questionAnswers = this.familyDiseaseCheck.questionAnswers
      .filter(ans => ans.choiceId !== '1610' && ans.choiceId !== '1611');

      this.showFamilyHistoryError = false;
    }

    // 步驟3：自覺症狀驗證
    if (this.step === 3) {
      const hasSubjectiveSymptom = this.subjectiveSymptomNoneOfFollowing ||
                                  this.subjectiveSymptomDiseases.length > 0 ||
                                  (this.otherSubjectiveSymptomDisease && this.otherSubjectiveSymptomDisease.trim());

      if (!hasSubjectiveSymptom) {
        this.showSubjectiveSymptomError = true;
        this.openRequiredFieldDialog();
        return;
      }

      this.subjectiveSymptomDiseaseCheck.questionAnswers = this.subjectiveSymptomDiseaseCheck.questionAnswers
      .filter(ans => ans.choiceId !== '1538' && ans.choiceId !== '1539');

      this.showSubjectiveSymptomError = false;
    }

    // 步驟4：長期服用藥物驗證
    if (this.step === 4) {
      // 檢查是否有勾選藥物 OR 有填寫其他藥物
      const hasLongTermMedication = this.longTermMedicationNoneOfFollowing || 
                                    this.longTermMedications.length > 0 ||
                                    (this.otherLongTermMedication && this.otherLongTermMedication.trim());
      
      if (!hasLongTermMedication) {
        this.showLongTermMedicationError = true; 
        this.openRequiredFieldDialog();
        return;
      }
      
      this.showLongTermMedicationError = false; 
      this.showError = false;
    }

    // 步驟5：手術史或重大外傷驗證
    if (this.step === 5) {
      if (!this.hasSurgeryHistoryOrMajorTrauma) {
        this.openRequiredFieldDialog();
        return;
      }

      if (this.hasSurgeryHistoryOrMajorTrauma === API_PARAMS.YES &&
          (!this.surgeryHistoryOrMajorTrauma || !this.surgeryHistoryOrMajorTrauma.trim())) {
        this.showError = true;
        return;
      }

      this.showError = false;
    }

    // 步驟6：生活習慣驗證
    if (this.step === 6) {
      if (!this.isLifeHabitValid() || !this.isExerciseHabitValid() || !this.isLifeStyleNumberInputValid()) {
        this.setLifeStyleErrors();
        this.setExerciseHabitErrors();
        this.showError = true;
        return;
      }
      this.showError = false;
    }

    // 步驟7：作業經歷驗證
    if (this.step === 7) {
      // 只檢查「有填寫的欄位」是否有負數
      const avgWorkHoursPastMonth = this.previousOccupation.get('avgWorkHoursPastMonth');
      const avgWorkHoursPastSixMonths = this.previousOccupation.get('avgWorkHoursPastSixMonths');
      const avgSleepTime = this.previousOccupation.get('avgSleepTime');

      const hasInvalidWorkHours = 
        (avgWorkHoursPastMonth?.value !== null && 
        avgWorkHoursPastMonth?.value !== 0 && 
        avgWorkHoursPastMonth?.invalid) ||
        (avgWorkHoursPastSixMonths?.value !== null && 
        avgWorkHoursPastSixMonths?.value !== 0 && 
        avgWorkHoursPastSixMonths?.invalid) ||
        (avgSleepTime?.value !== null && 
        avgSleepTime?.value !== 0 && 
        avgSleepTime?.invalid);

      if (hasInvalidWorkHours) {
        this.showError = true;
        return;
      }
    }

    this.showError = false;
    this.step++;
    this.scrollToTop();
  }

  /**
   * 收集所有問題的答案
   */
  private collectAllQuestions(): Questions[] {
      const questions: Questions[] = [];
    
    // ========== 步驟 1-3：個人病史、家族病史、自覺症狀 ==========
    
    // 1. 個人病史 (questionId: 474)
    if (this.personalDiseaseCheck.questionAnswers.length > 0 || this.otherPersonalDisease) {
      // 過濾掉詳細說明的 choiceId (1568骨折, 1569癌症, 1570其他慢性病)
      const personalAnswers = this.personalDiseaseCheck.questionAnswers.filter(
        ans => ans.choiceId !== '1568' && ans.choiceId !== '1569' && ans.choiceId !== '1570'
      );

      console.log('儲存個人病史資料');

      questions.push({
        questionId: '474',
        questionAnswers: personalAnswers
      });
      
      // 其他個人疾病說明 (questionId: 478)
      if (this.otherPersonalDisease && this.otherPersonalDisease.trim()) {
        questions.push({
          questionId: '478',
          questionAnswers: [{
            choiceId: '1571',
            answer: this.otherPersonalDisease
          }]
        });
      }
    } else {
      console.log('個人病史沒有資料可儲存');
    }

    // 癌症詳細說明 (questionId: 476)
    if (this.personalDiseaseDetails['1565']?.trim()) {
      questions.push({
        questionId: '476',
        questionAnswers: [{
          choiceId: '1569',
          answer: this.personalDiseaseDetails['1565']
        }]
      });
    }
    
    // 骨折詳細說明 (questionId: 475)
    if (this.personalDiseaseDetails['1564']?.trim()) {
      questions.push({
        questionId: '475',
        questionAnswers: [{
          choiceId: '1568',
          answer: this.personalDiseaseDetails['1564']
        }]
      });
    }
    
    // 其他慢性疾病詳細說明 (questionId: 477)
    if (this.personalDiseaseDetails['1566']?.trim()) {
      questions.push({
        questionId: '477',
        questionAnswers: [{
          choiceId: '1570',
          answer: this.personalDiseaseDetails['1566']
        }]
      });
    }

    // ========== 步驟 2:家族病史 ==========
    // 家族病史 (questionId: 483)
    if (this.familyDiseaseCheck.questionAnswers.length > 0 || this.otherFamilyDisease) {
      const familyAnswers = [...this.familyDiseaseCheck.questionAnswers];
      
      // 如果有填寫其他家族病史,要在 483 加上 choiceId 1610 (表示勾選了「其他」選項)
      if (this.otherFamilyDisease && this.otherFamilyDisease.trim()) {
        // 檢查是否已經有 1610,避免重複
        const hasOtherChoice = familyAnswers.find(ans => ans.choiceId === '1610');
        if (!hasOtherChoice) {
          familyAnswers.push({
            choiceId: '1610',
            answer: '19'
          });
        }
      }
      
      questions.push({
        questionId: '483',
        questionAnswers: familyAnswers
      });
      
      console.log('儲存家族病史資料:', {
        questionId: '483',
        questionAnswers: familyAnswers,
        otherFamilyDisease: this.otherFamilyDisease
      });
    }

    // 其他家族病史說明 (questionId: 484)
    if (this.otherFamilyDisease && this.otherFamilyDisease.trim()) {
      questions.push({
        questionId: '484',
        questionAnswers: [{
          choiceId: '1611',
          answer: this.otherFamilyDisease
        }]
      });
    }
    
    // 3. 自覺症狀 (questionId: 472)
    if (this.subjectiveSymptomDiseaseCheck.questionAnswers.length > 0 || this.otherSubjectiveSymptomDisease) {
      const subjectiveAnswers = [...this.subjectiveSymptomDiseaseCheck.questionAnswers];
      
      // 如果有填寫其他自覺症狀,要在 472 加上 choiceId 1538 (表示勾選了「其他」選項)
      if (this.otherSubjectiveSymptomDisease && this.otherSubjectiveSymptomDisease.trim()) {
        // 檢查是否已經有 1538,避免重複
        const hasOtherChoice = subjectiveAnswers.find(ans => ans.choiceId === '1538');
        if (!hasOtherChoice) {
          subjectiveAnswers.push({
            choiceId: '1538',
            answer: '29'
          });
        }
      }

      questions.push({
        questionId: '472',
        questionAnswers: subjectiveAnswers
      });
      
      // 其他自覺症狀說明 (questionId: 473)
      if (this.otherSubjectiveSymptomDisease && this.otherSubjectiveSymptomDisease.trim()) {
        questions.push({
          questionId: '473',
          questionAnswers: [{
            choiceId: '1539',
            answer: this.otherSubjectiveSymptomDisease
          }]
        });
      }

      console.log('儲存自覺症狀資料:', {
        questionId: '472',
        questionAnswers: subjectiveAnswers,
        otherSubjectiveSymptomDisease: this.otherSubjectiveSymptomDisease
      });
    }
    
    // ========== 步驟 4:長期服用藥物 ==========
    // 長期服用藥物 (questionId: 481)
    if (this.longTermMedicationCheck.questionAnswers.length > 0 || this.otherLongTermMedication) {
      const medicationAnswers = [...this.longTermMedicationCheck.questionAnswers];
      
      // 如果有填寫其他藥物,要在 481 加上 choiceId 1590 (表示勾選了「其他」選項)
      if (this.otherLongTermMedication && this.otherLongTermMedication.trim()) {
        // 檢查是否已經有 1590,避免重複
        const hasOtherChoice = medicationAnswers.find(ans => ans.choiceId === '1590');
        if (!hasOtherChoice) {
          medicationAnswers.push({
            choiceId: '1590',
            answer: '16'
          });
        }
      }
      
      questions.push({
        questionId: '481',
        questionAnswers: medicationAnswers
      });
      
      console.log('儲存長期服用藥物資料:', {
        questionId: '481',
        questionAnswers: medicationAnswers,
        otherLongTermMedication: this.otherLongTermMedication
      });
    }

    // 其他藥物說明 (questionId: 482)
    // 只要有填寫就保存，不需要勾選「其他藥物」checkbox
    if (this.otherLongTermMedication?.trim()) {
      questions.push({
        questionId: '482',
        questionAnswers: [{
          choiceId: '1591',
          answer: this.otherLongTermMedication
        }]
      });
    }
    
    // ========== 步驟 5：手術史或重大外傷 ==========
    
    // 是否有手術史 (questionId: 479)
    if (this.hasSurgeryHistoryOrMajorTrauma) {
      const hasChoiceId = this.hasSurgeryHistoryOrMajorTrauma === API_PARAMS.YES ? '1573' : '1572';
      const hasAnswer = this.hasSurgeryHistoryOrMajorTrauma === API_PARAMS.YES ? '2' : '1';
      
      questions.push({
        questionId: '479',
        questionAnswers: [{
          choiceId: hasChoiceId,
          answer: hasAnswer
        }]
      });
      
      // 手術史詳細說明 (questionId: 480)
      if (this.hasSurgeryHistoryOrMajorTrauma === API_PARAMS.YES && this.surgeryHistoryOrMajorTrauma?.trim()) {
        questions.push({
          questionId: '480',
          questionAnswers: [{
            choiceId: '1574',
            answer: this.surgeryHistoryOrMajorTrauma
          }]
        });
      }
    }
    
    // ========== 步驟 6：生活習慣 ==========
    
    // 檳榔 (questionId: 485)
    const betelNutHabit = this.selectedLifeStyleHabits[0];
    if (betelNutHabit) {
      const betelOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.name === betelNutHabit);
      
      if (betelOption) {
        questions.push({
          questionId: '485',
          questionAnswers: [{
            choiceId: betelOption.choiceId,
            answer: betelOption.answer
          }]
        });
        
        // 如果選擇"(幾乎)每天嚼"，加入數字欄位
        if (betelNutHabit === '幾乎每天嚼') {
          if (this.betelNutPerDay.value) {
            questions.push({
              questionId: '486',
              questionAnswers: [{ choiceId: '1616', answer: String(this.betelNutPerDay.value) }]
            });
          }
          if (this.betelNutYears.value) {
            questions.push({
              questionId: '487',
              questionAnswers: [{ choiceId: '1617', answer: String(this.betelNutYears.value) }]
            });
          }
          if (this.betelNutMonths.value) {
            questions.push({
              questionId: '489',
              questionAnswers: [{ choiceId: '1619', answer: String(this.betelNutMonths.value) }]
            });
          }
        }
        
        // 如果選擇"已經戒食"，加入戒除年數和月數
        if (betelNutHabit === '已經戒食') {
          if (this.betelNutQuitYears.value) {
            questions.push({
              questionId: '488',
              questionAnswers: [{ choiceId: '1618', answer: String(this.betelNutQuitYears.value) }]
            });
          }
          // 月份欄位：只要有填寫年份就要儲存月份（即使月份為0）
          if (this.betelNutQuitYears.value) {
            questions.push({
              questionId: '489',
              questionAnswers: [{ choiceId: '1619', answer: String(this.betelNutQuitMonths.value || 0) }]
            });
          }
        }
      }
    }
    
    // 抽菸 (questionId: 490)
    const smokingHabit = this.selectedLifeStyleHabits[1];
    if (smokingHabit) {
      const smokingOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[1].diseaseOptions
        .find(opt => opt.name === smokingHabit);
      
      if (smokingOption) {
        questions.push({
          questionId: '490',
          questionAnswers: [{
            choiceId: smokingOption.choiceId,
            answer: smokingOption.answer
          }]
        });
        
        // 如果選擇"(幾乎)每天吸"，加入數字欄位
        if (smokingHabit === '(幾乎)每天吸') {
          if (this.cigarettesPerDay.value) {
            questions.push({
              questionId: '491',
              questionAnswers: [{ choiceId: '1624', answer: String(this.cigarettesPerDay.value) }]
            });
          }
          if (this.smokingYears.value) {
            questions.push({
              questionId: '492',
              questionAnswers: [{ choiceId: '1625', answer: String(this.smokingYears.value) }]
            });
          }
        }
        
        // 如果選擇"已經戒菸"，加入戒除年數和月數
        if (smokingHabit === '已經戒菸') {
          if (this.smokingQuitYears.value) {
            questions.push({
              questionId: '493',
              questionAnswers: [{ choiceId: '1626', answer: String(this.smokingQuitYears.value) }]
            });
          }
          // 月份欄位：只要有填寫年份就要儲存月份（即使月份為0）
          if (this.smokingQuitYears.value) {
            questions.push({
              questionId: '494',
              questionAnswers: [{ choiceId: '1627', answer: String(this.smokingQuitMonths.value || 0) }]
            });
          }
        }
      }
    }
    
    // 喝酒 (questionId: 495)
    const alcoholHabit = this.selectedLifeStyleHabits[2];
    if (alcoholHabit) {
      const alcoholOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[2].diseaseOptions
        .find(opt => opt.name === alcoholHabit);
      
      if (alcoholOption) {
        questions.push({
          questionId: '495',
          questionAnswers: [{
            choiceId: alcoholOption.choiceId,
            answer: alcoholOption.answer
          }]
        });
        
        // 如果選擇"(幾乎)每天喝"，加入數字欄位
        if (alcoholHabit === '(幾乎)每天喝') {
          if (this.alcoholTimesPerWeek.value) {
            questions.push({
              questionId: '496',
              questionAnswers: [{ choiceId: '1632', answer: String(this.alcoholTimesPerWeek.value) }]
            });
          }
          if (this.alcoholType.value?.trim()) {
            questions.push({
              questionId: '497',
              questionAnswers: [{ choiceId: '1633', answer: this.alcoholType.value }]
            });
          }
          if (this.alcoholAmountCc.value) {
            questions.push({
              questionId: '498',
              questionAnswers: [{ choiceId: '1634', answer: String(this.alcoholAmountCc.value) }]
            });
          }
        }
        
        // 如果選擇"已經戒酒"，加入戒除年數和月數
        if (alcoholHabit === '已經戒酒') {
          if (this.alcoholQuitYears.value) {
            questions.push({
              questionId: '499',
              questionAnswers: [{ choiceId: '1635', answer: String(this.alcoholQuitYears.value) }]
            });
          }
          // 月份欄位：只要有填寫年份就要儲存月份（即使月份為0）
          if (this.alcoholQuitYears.value) {
            questions.push({
              questionId: '500',
              questionAnswers: [{ choiceId: '1636', answer: String(this.alcoholQuitMonths.value || 0) }]
            });
          }
        }
      }
    }

    // 飲食習慣 (questionId: 501)
    const dietHabits = this.lifeStyleHabits.filter(habit => 
      habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseType)
    );
    
    if (dietHabits.length > 0) {
      const dietAnswers: QuestionAnswers[] = [];
      
      dietHabits.forEach(habit => {
        const habitName = habit.split('_')[1];
        const dietOption = HEALTH_QUESTIONNAIRE_FIELDS.LIFESTYLE_HABITS.diseaseGroups[3].diseaseOptions
          .find(opt => opt.name === habitName);
        
        if (dietOption) {
          dietAnswers.push({
            choiceId: dietOption.choiceId,
            answer: dietOption.answer
          });
        }
      });
      
      if (dietAnswers.length > 0) {
        questions.push({
          questionId: '501',
          questionAnswers: dietAnswers
        });
      }
    }
    
    // ========== 步驟 6：運動習慣 ==========
    
    // 1. 是否有運動習慣 (questionId: 502)
    if (this.hasExerciseHabit) {
      const exerciseOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[0].diseaseOptions
        .find(opt => opt.name.includes(this.hasExerciseHabit === API_PARAMS.NO ? '無' : '有'));
      
      if (exerciseOption) {
        questions.push({
          questionId: '502',
          questionAnswers: [{
            choiceId: exerciseOption.choiceId,
            answer: exerciseOption.answer
          }]
        });
      }
      
      // 如果沒有運動習慣，處理問題 2-5
      if (this.hasExerciseHabit === API_PARAMS.NO) {
        // 2. 能夠舒適走多久 (questionId: 503)
        if (this.selectedExerciseHabits[1]) {
          const walkOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[1].diseaseOptions
            .find(opt => this.selectedExerciseHabits[1].includes(opt.name));
          if (walkOption) {
            questions.push({
              questionId: '503',
              questionAnswers: [{ choiceId: walkOption.choiceId, answer: walkOption.answer }]
            });
          }
        }
        
        // 3. 能夠提重物 (questionId: 504)
        if (this.selectedExerciseHabits[2]) {
          const liftOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[2].diseaseOptions
            .find(opt => this.selectedExerciseHabits[2].includes(opt.name));
          if (liftOption) {
            questions.push({
              questionId: '504',
              questionAnswers: [{ choiceId: liftOption.choiceId, answer: liftOption.answer }]
            });
          }
        }
        
        // 4. 運動喜好 (questionId: 505)
        const exercisePreferences = this.exerciseHabits.filter(habit => 
          habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[3].diseaseType)
        );
        
        if (exercisePreferences.length > 0) {
          const preferenceAnswers: QuestionAnswers[] = [];
          exercisePreferences.forEach(habit => {
            const habitName = habit.split('_').slice(1).join('_');
            const prefOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[3].diseaseOptions
              .find(opt => opt.name === habitName);
            if (prefOption) {
              preferenceAnswers.push({
                choiceId: prefOption.choiceId,
                answer: prefOption.answer
              });
            }
          });
          
          if (preferenceAnswers.length > 0) {
            questions.push({
              questionId: '505',
              questionAnswers: preferenceAnswers
            });
          }
        }
        
        // 5. 運動禁忌 (questionId: 506) - 可複選
        const prohibitionHabits = this.exerciseHabits.filter(habit =>
          habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseType)
        );

        if (prohibitionHabits.length > 0) {
          const prohibitionAnswers: QuestionAnswers[] = [];
          prohibitionHabits.forEach(habit => {
            const habitName = habit.split('_').slice(1).join('_');
            const prohibitionOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseOptions
              .find(opt => opt.name === habitName);
            if (prohibitionOption) {
              prohibitionAnswers.push({
                choiceId: prohibitionOption.choiceId,
                answer: prohibitionOption.answer
              });
            }
          });

          if (prohibitionAnswers.length > 0) {
            questions.push({
              questionId: '506',
              questionAnswers: prohibitionAnswers
            });
            console.log('儲存運動禁忌（無運動習慣）:', prohibitionHabits);
          }
        }
      }
      
      // 如果有運動習慣，處理問題 5-9
      if (this.hasExerciseHabit === API_PARAMS.YES) {
        // 5. 運動禁忌 (questionId: 506) - 可複選
        const prohibitionHabits = this.exerciseHabits.filter(habit =>
          habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseType)
        );

        if (prohibitionHabits.length > 0) {
          const prohibitionAnswers: QuestionAnswers[] = [];
          prohibitionHabits.forEach(habit => {
            const habitName = habit.split('_').slice(1).join('_');
            const prohibitionOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[4].diseaseOptions
              .find(opt => opt.name === habitName);
            if (prohibitionOption) {
              prohibitionAnswers.push({
                choiceId: prohibitionOption.choiceId,
                answer: prohibitionOption.answer
              });
            }
          });

          if (prohibitionAnswers.length > 0) {
            questions.push({
              questionId: '506',
              questionAnswers: prohibitionAnswers
            });
            console.log('儲存運動禁忌（有運動習慣）:', prohibitionHabits);
          }
        }
        
        // 6. 通常進行哪種運動 (questionId: 507)
        const actualExercises = this.exerciseHabits.filter(habit => 
          habit.startsWith(HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[5].diseaseType)
        );
        
        if (actualExercises.length > 0) {
          const exerciseAnswers: QuestionAnswers[] = [];
          actualExercises.forEach(habit => {
            const habitName = habit.split('_').slice(1).join('_');
            const exOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[5].diseaseOptions
              .find(opt => opt.name === habitName);
            if (exOption) {
              exerciseAnswers.push({
                choiceId: exOption.choiceId,
                answer: exOption.answer
              });
            }
          });
          
          if (exerciseAnswers.length > 0) {
            questions.push({
              questionId: '507',
              questionAnswers: exerciseAnswers
            });
          }
        }
        
        // 7. 每周運動時間 (questionId: 508)
        if (this.selectedExerciseHabits[6]) {
          const weeklyOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[6].diseaseOptions
            .find(opt => this.selectedExerciseHabits[6].includes(opt.name));
          if (weeklyOption) {
            questions.push({
              questionId: '508',
              questionAnswers: [{ choiceId: weeklyOption.choiceId, answer: weeklyOption.answer }]
            });
          }
        }
        
        // 8. 每次運動時間 (questionId: 509)
        if (this.selectedExerciseHabits[7]) {
          const durationOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[7].diseaseOptions
            .find(opt => this.selectedExerciseHabits[7].includes(opt.name));
          if (durationOption) {
            questions.push({
              questionId: '509',
              questionAnswers: [{ choiceId: durationOption.choiceId, answer: durationOption.answer }]
            });
          }
        }
        
        // 9. 運動強度 (questionId: 510)
        if (this.selectedExerciseHabits[8]) {
          const intensityOption = HEALTH_QUESTIONNAIRE_FIELDS.EXERCISE_HABITS.diseaseGroups[8].diseaseOptions
            .find(opt => this.selectedExerciseHabits[8].includes(opt.name));
          if (intensityOption) {
            questions.push({
              questionId: '510',
              questionAnswers: [{ choiceId: intensityOption.choiceId, answer: intensityOption.answer }]
            });
          }
        }
      }
    }

    // ========== 步驟 7：作業經歷 ==========

    // 1. 曾經從事行業
    if (this.previousOccupation.value.category) {
      const prevCategoryOption = PREVIOUS_OCCUPATION_CATEGORY_OPTIONS.find(
        opt => opt.answer === this.previousOccupation.value.category
      );
      if (prevCategoryOption) {
        questions.push({
          questionId: '519',
          questionAnswers: [{
            choiceId: prevCategoryOption.choiceId,
            answer: prevCategoryOption.answer
          }]
        });
      }
    }

    // 2. 曾經從事行業其他說明 - 只要有填寫就保存
    if (this.previousOccupation.value.category === '19' && this.previousOccupation.value.otherOccupation?.trim()) {
      questions.push({
        questionId: '520',
        questionAnswers: [{
          choiceId: '1721',
          answer: this.previousOccupation.value.otherOccupation.trim()
        }]
      });
    }

    // 3. 開始年月
    if (this.previousOccupation.value.startYear && this.previousOccupation.value.startMonth) {
      const startDate = `${this.previousOccupation.value.startYear}/${String(this.previousOccupation.value.startMonth).padStart(2, '0')}`;
      questions.push({
        questionId: '521',
        questionAnswers: [{
          choiceId: '1722',
          answer: startDate
        }]
      });
    }

    // 4. 結束年月 - 只要有填寫就保存
    if (this.previousOccupation.value.endYear && this.previousOccupation.value.endMonth) {
      const endDate = `${this.previousOccupation.value.endYear}/${String(this.previousOccupation.value.endMonth).padStart(2, '0')}`;
      questions.push({
        questionId: '522',
        questionAnswers: [{
          choiceId: '1723',
          answer: endDate
        }]
      });
    }

    // 5. 目前從事行業
    if (this.occupation.value.category) {
      const categoryOption = CURRENT_OCCUPATION_CATEGORY_OPTIONS.find(
        opt => opt.answer === this.occupation.value.category
      );
      if (categoryOption) {
        questions.push({
          questionId: '523',
          questionAnswers: [{
            choiceId: categoryOption.choiceId,
            answer: categoryOption.answer
          }]
        });
      }
    }

    // 6. 目前從事行業其他說明 - 只要有填寫就保存
    if (this.occupation.value.category === '19' && this.occupation.value.otherOccupation?.trim()) {
      questions.push({
        questionId: '524',
        questionAnswers: [{
          choiceId: '1743',
          answer: this.occupation.value.otherOccupation.trim()
        }]
      });
    }

    // 7. 受僱日期起
    if (this.occupation.value.employmentDate) {
      const dateStr = this.occupation.value.employmentDate.format('YYYY/MM');
      questions.push({
        questionId: '525',
        questionAnswers: [{
          choiceId: '1744',
          answer: dateStr
        }]
      });
    }

    // 8. 受僱日期止
    if (this.occupation.value.employmentEndDate) {
      const dateStr = this.occupation.value.employmentEndDate.format('YYYY/MM');
      questions.push({
        questionId: '526',
        questionAnswers: [{
          choiceId: '1745',
          answer: dateStr
        }]
      });
    }

    // 9. 是否需輪班 (questionId: 527) 和 輪班制 (questionId: 528)
    if (this.occupation.value.type) {
      const typeValue = this.occupation.value.type;
      console.log('儲存輪班資料，當前值:', typeValue);

      if (typeValue === 'needs_shift_no') {
        // 選擇了「否」，只保存 questionId 527，不保存 questionId 528
        const needsShiftNoOption = NEEDS_SHIFT_OPTIONS.find(opt => opt.answer === '1');
        if (needsShiftNoOption) {
          questions.push({
            questionId: '527',
            questionAnswers: [{
              choiceId: needsShiftNoOption.choiceId,
              answer: needsShiftNoOption.answer
            }]
          });
          console.log('儲存輪班選項：否 (不保存輪班制選項)');
        }
      } else if (typeValue.startsWith('shift_type_')) {
        // 選擇了輪班制（兩班制/三班制/四班制/其他）
        const shiftTypeAnswer = typeValue.replace('shift_type_', '');
        const shiftTypeOption = SHIFT_TYPE_OPTIONS.find(opt => opt.answer === shiftTypeAnswer);

        if (shiftTypeOption) {
          // 保存「是」(answer='2', choiceId='1747')
          const needsShiftYesOption = NEEDS_SHIFT_OPTIONS.find(opt => opt.answer === '2');
          if (needsShiftYesOption) {
            questions.push({
              questionId: '527',
              questionAnswers: [{
                choiceId: needsShiftYesOption.choiceId,
                answer: needsShiftYesOption.answer
              }]
            });
          }

          // 保存具體的輪班制選項
          questions.push({
            questionId: '528',
            questionAnswers: [{
              choiceId: shiftTypeOption.choiceId,
              answer: shiftTypeOption.answer
            }]
          });

          console.log('儲存輪班選項：', shiftTypeOption.name);
        }
      }
    }

    // 11. 輪班方式說明 - 只有在選擇「其他」輪班制時才保存
    if (this.occupation.value.type === 'shift_type_4' && this.occupation.value.shiftDescription?.trim()) {
      questions.push({
        questionId: '529',
        questionAnswers: [{
          choiceId: '1752',
          answer: this.occupation.value.shiftDescription.trim()
        }]
      });
    }

    // 12. 過去一個月平均工時
    if (this.previousOccupation.value.avgWorkHoursPastMonth && 
        this.previousOccupation.value.avgWorkHoursPastMonth > 0) {
      questions.push({
        questionId: '530',
        questionAnswers: [{
          choiceId: '1753',
          answer: String(this.previousOccupation.value.avgWorkHoursPastMonth)
        }]
      });
    }

    // 13. 過去六個月平均工時
    if (this.previousOccupation.value.avgWorkHoursPastSixMonths && 
        this.previousOccupation.value.avgWorkHoursPastSixMonths > 0) {
      questions.push({
        questionId: '531',
        questionAnswers: [{
          choiceId: '1754',
          answer: String(this.previousOccupation.value.avgWorkHoursPastSixMonths)
        }]
      });
    }

    // 14. 檢查原因
    if (this.previousOccupation.value.examReason) {
      const examReasonOption = EXAM_REASON_OPTIONS.find(
        opt => opt.answer === this.previousOccupation.value.examReason
      );
      if (examReasonOption) {
        questions.push({
          questionId: '532',
          questionAnswers: [{
            choiceId: examReasonOption.choiceId,
            answer: examReasonOption.answer
          }]
        });
      }
    }

    // 15. 平均睡眠時間
    if (this.previousOccupation.value.avgSleepTime && 
        this.previousOccupation.value.avgSleepTime > 0) {
      questions.push({
        questionId: '533',
        questionAnswers: [{
          choiceId: '1757',
          answer: String(this.previousOccupation.value.avgSleepTime)
        }]
      });
    }
    
    // ========== 步驟 8：其他資訊 ==========
    
    // 常住國家 (questionId: 511)
    if (this.residenceCountries.length > 0) {
      const countryAnswers: QuestionAnswers[] = [];

      this.residenceCountries.forEach(countryValue => {
        // residenceCountries 存的是 answer 值，不是 name
        const countryOption = RESIDENCE_COUNTRY_OPTIONS.find(opt => opt.answer === countryValue);
        if (countryOption) {
          countryAnswers.push({
            choiceId: countryOption.choiceId,
            answer: countryOption.answer
          });
        }
      });
      
      if (countryAnswers.length > 0) {
        questions.push({
          questionId: '511',
          questionAnswers: countryAnswers
        });
      }
    }
    
    // 常住國家其他 (questionId: 512)
    if (this.residenceCountries.includes('6') && this.otherResidenceCountry?.trim()) {
      questions.push({
        questionId: '512',
        questionAnswers: [{
          choiceId: '1680',
          answer: this.otherResidenceCountry
        }]
      });
    }
    
    // 婚姻狀況 (questionId: 513)
    if (this.maritalStatus) {
      const maritalOption = MARITAL_STATUS_OPTIONS.find(opt => opt.answer === this.maritalStatus);
      if (maritalOption) {
        questions.push({
          questionId: '513',
          questionAnswers: [{
            choiceId: maritalOption.choiceId,
            answer: maritalOption.answer
          }]
        });
      }
    }
    
    // 教育程度 (questionId: 514)
    if (this.educationLevel) {
      const educationOption = EDUCATION_LEVEL_OPTIONS.find(opt => opt.answer === this.educationLevel);
      if (educationOption) {
        questions.push({
          questionId: '514',
          questionAnswers: [{
            choiceId: educationOption.choiceId,
            answer: educationOption.answer
          }]
        });
      }
    }
    
    // 職位 (questionId: 515)
    if (this.position) {
      const positionOption = POSITION_OPTIONS.find(opt => opt.answer === this.position);
      if (positionOption) {
        questions.push({
          questionId: '515',
          questionAnswers: [{
            choiceId: positionOption.choiceId,
            answer: positionOption.answer
          }]
        });
      }
    }
    
    // 職位其他 (questionId: 516)
    if (this.position === '9' && this.otherPosition?.trim()) {
      questions.push({
        questionId: '516',
        questionAnswers: [{
          choiceId: '1699',
          answer: this.otherPosition
        }]
      });
    }

    console.log('收集到的所有問題:', questions.length, '個');
    questions.forEach(q => console.log(`questionId: ${q.questionId}, answers: ${q.questionAnswers.length}`));
    
    return questions;
  }

  // 點擊"儲存"
  save() {
    if (this.step === 8) {
      let hasError = false;

      // 驗證常住國家
      const hasResidenceCountry = this.residenceCountries.length > 0;
      const hasOtherResidenceCountry = this.residenceCountries.includes('6');

      if (!hasResidenceCountry) {
        this.showResidenceCountryError = true;
        hasError = true;
      } else if (hasOtherResidenceCountry && (!this.otherResidenceCountry || !this.otherResidenceCountry.trim())) {
        this.showResidenceCountryError = true;
        hasError = true;
      } else {
        this.showResidenceCountryError = false;
      }

      // 驗證婚姻狀況
      if (!this.maritalStatus) {
        this.showMaritalStatusError = true;
        hasError = true;
      } else {
        this.showMaritalStatusError = false;
      }

      // 驗證教育程度
      if (!this.educationLevel) {
        this.showEducationLevelError = true;
        hasError = true;
      } else {
        this.showEducationLevelError = false;
      }

      // 驗證職位
      if (!this.position) {
        this.showPositionError = true;
        hasError = true;
      } else if (this.position === '9' && (!this.otherPosition || !this.otherPosition.trim())) {
        // 如果選擇「其他」，必須填寫內容
        this.showPositionError = true;
        hasError = true;
      } else {
        this.showPositionError = false;
      }

      // 如果有任何錯誤，不執行儲存
      if (hasError) {
        this.showError = true;
        return;
      }

      // 清除所有錯誤狀態
      this.showError = false;

      // API 請求資料
      const saveRequest: SaveQuestionnaireRequest = {
        idNo: this.sharedStorageService.get('idLoginRequest')?.idNo ?? 
              this.sharedStorageService.get('createMemberRequest')?.idNo ?? '',
        // companyNo: this.appointmentInfo?.companyNo,
        caseNo: this.appointmentInfo?.caseNo ?? '',
        questionnaireId: 20,
        entQuestionnaireId: 681,
        questions: this.collectAllQuestions()
      };

      console.log('準備送出的健調表資料:', JSON.stringify(saveRequest, null, 2));

      // 呼叫 API 儲存
      this.reserveHealthService.saveQuestionnaire(saveRequest).subscribe({
        next: (response) => {
          if (response.statusCode === 'S0000') {
            this.handleSaveSuccess();
          } else {
            this.handleSaveError(response.errorMessage);
          }
        },
        error: (error) => {
          console.error('儲存健調表發生錯誤:', error);
          this.handleSaveError();
        }
      });
    }
  }

  /**
   * 處理儲存成功
   */
  private handleSaveSuccess(): void {
    this.isFormDirty = false;
    this.allowNavigation = false;

    // 儲存成功後，設定為已完成狀態
    // 這樣未完成標籤就會消失，但仍然可以重新填寫
    this.questionnaireStatus = 'COMPLETED';
    this.reserveHealthService.updateQuestionnaireStatus('COMPLETED');

    // 將完整的健調表資料儲存到 SharedStorage，解決 CB-022 和 CB-043 不同步問題
    this.saveQuestionnaireDataToStorage();

    // 顯示成功訊息
    this.toastr.success(this.translateService.instant('health-questionnaire.save-success'));

    this.step = 1;
    this.scrollToTop();
  }

  /**
   * 將健調表資料儲存到 SharedStorage
   */
  private saveQuestionnaireDataToStorage(): void {
    if (!this.appointmentInfo?.caseNo) {
      console.log('沒有 caseNo，跳過儲存到 SharedStorage');
      return;
    }

    const questionnaireData = {
      caseNo: this.appointmentInfo.caseNo,
      questions: this.collectAllQuestions(),
      savedAt: new Date().toISOString()
    };

    this.sharedStorageService.set('savedQuestionnaireData', questionnaireData as any);
    console.log('健調表資料已儲存到 SharedStorage:', questionnaireData);
  }

  /**
   * 從 SharedStorage 載入健調表資料
   */
  private loadQuestionnaireDataFromStorage(): boolean {
    const savedData = this.sharedStorageService.get('savedQuestionnaireData');

    console.log('檢查 SharedStorage 資料:', savedData);
    console.log('當前 appointmentInfo:', this.appointmentInfo);

    if (!savedData) {
      console.log('SharedStorage 中沒有健調表資料');
      return false;
    }

    if (!this.appointmentInfo?.caseNo) {
      console.log('沒有 caseNo，但 SharedStorage 有資料，稍後重試');
      return false;
    }

    // 檢查是否為同一個案件
    if (savedData.caseNo !== this.appointmentInfo.caseNo) {
      console.log('SharedStorage 中的資料是不同案件，清除舊資料');
      this.sharedStorageService.remove('savedQuestionnaireData');
      return false;
    }

    // 檢查資料是否過期（超過 24 小時）
    const savedTime = new Date(savedData.savedAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - savedTime.getTime()) / (1000 * 60 * 60);

    if (hoursDiff > 24) {
      console.log('SharedStorage 中的資料已過期，清除舊資料');
      this.sharedStorageService.remove('savedQuestionnaireData');
      return false;
    }

    console.log('從 SharedStorage 載入健調表資料');
    // 先載入資料
    this.loadOtherStepsData(savedData.questions);
    // 載入完資料後才重新判斷狀態
    return true;
  }



  /**
   * 處理儲存失敗
   */
  private handleSaveError(errorMessage?: string): void {
    const message = errorMessage || this.translateService.instant('health-questionnaire.save-error-message');
    this.toastr.error(message, this.translateService.instant('health-questionnaire.save-failed'));
  }

  isLifeStyleNumberInputValid(): boolean {
    // 檳榔相關驗證
    if (this.selectedLifeStyleHabits[0] === '幾乎每天嚼') {
      if (!this.betelNutPerDay.valid || !this.betelNutYears.valid || !this.betelNutMonths.valid) {
        return false;
      }
    }
    if (this.selectedLifeStyleHabits[0] === '已經戒食') {
      if (!this.betelNutQuitYears.valid || !this.betelNutQuitMonths.valid) {
        return false;
      }
    }

    // 抽菸相關驗證
    if (this.selectedLifeStyleHabits[1] === '(幾乎)每天吸') {
      if (!this.cigarettesPerDay.valid || !this.smokingYears.valid) {
        return false;
      }
    }
    if (this.selectedLifeStyleHabits[1] === '已經戒菸') {
      if (!this.smokingQuitYears.valid || !this.smokingQuitMonths.valid) {
        return false;
      }
    }

    // 喝酒相關驗證
    if (this.selectedLifeStyleHabits[2] === '(幾乎)每天喝') {
      if (!this.alcoholTimesPerWeek.valid || !this.alcoholAmountCc.valid || !this.alcoholType.value?.trim()) {
        return false;
      }
    }
    if (this.selectedLifeStyleHabits[2] === '已經戒酒') {
      if (!this.alcoholQuitYears.valid || !this.alcoholQuitMonths.valid) {
        return false;
      }
    }

    return true;
  }

  resetLifeStyleNumberInputs(habitType: number): void {
    switch (habitType) {
      case 0: // 檳榔
        this.betelNutPerDay.reset(0);
        this.betelNutYears.reset(0);
        this.betelNutMonths.reset(0);
        this.betelNutQuitYears.reset(0);
        this.betelNutQuitMonths.reset(0);
        break;
      case 1: // 抽菸
        this.cigarettesPerDay.reset(0);
        this.smokingYears.reset(0);
        this.smokingQuitYears.reset(0);
        this.smokingQuitMonths.reset(0);
        break;
      case 2: // 喝酒
        this.alcoholTimesPerWeek.reset(0);
        this.alcoholType.reset('');
        this.alcoholAmountCc.reset(0);
        this.alcoholQuitYears.reset(0);
        this.alcoholQuitMonths.reset(0);
        break;
    }
  }

  openRequiredFieldDialog(): void {
    const title = this.translateService.instant('health-questionnaire.no-field-been-checked');
    const buttonText = this.translateService.instant('common.button.i-understood');
    
    const dialogRef = this.dialog.open(SingleActionDialogComponent,{
      data: {
        title: title,
        buttonText: buttonText,
        action: () => {
          dialogRef.close();
        }
      }
    });
  }

  /**
   * 標記表單已修改
   */
  markFormDirty(): void {
    if (this.questionnaireStatus === 'AVAILABLE') {
      this.isFormDirty = true;
    }
  }

  /**
   * 實作 CanDeactivate 介面
   */
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.allowNavigation || !this.isFormDirty) {
      return true;
    }
    return this.showUnsavedChangesDialog();
  }

  /**
   * 瀏覽器關閉/重整警告
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: BeforeUnloadEvent): void {
    if (this.isFormDirty && this.questionnaireStatus === 'AVAILABLE') {
      $event.returnValue = true;
    }
  }

  /**
   * 顯示未儲存變更對話框
   */
  private showUnsavedChangesDialog(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogData: DuoActionDialogModel = {
        title: this.translateService.instant('health-questionnaire.unsaved-changes-title'),
        message: this.translateService.instant('health-questionnaire.unsaved-changes-message'),
        leftButtonText: this.translateService.instant('health-questionnaire.continue-editing'),
        rightButtonText: this.translateService.instant('health-questionnaire.confirm-leave'),
        leftButtonAction: () => {
          dialogRef.close(); // 關閉 dialog
          resolve(false); // 繼續編輯
        },
        rightButtonAction: () => {
          this.allowNavigation = true;
          dialogRef.close(); // 關閉 dialog
          resolve(true); // 確定離開
        },
        showCloseButton: false
      };

      const dialogRef = this.dialog.open(DuoActionDialogComponent, {
        data: dialogData,
        disableClose: true
      });
    });
  }
}